import { ChangeEvent, useState, useEffect, useMemo } from "react";
import { EmployeeAcaSummary } from "@/components/aca/EmployeeAcaSummary";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowLeft,
  User,
  Users,
  Building2,
  Briefcase,
  Calendar,
  Clock,
  MapPin,
  Plus,
  Trash2,
  Edit3,
  Edit,
  Save,
  X,
  ChevronRight,
  DollarSign,
  Shield,
  Heart,
  Smile,
  Eye,
  Umbrella,
  CreditCard,
  IdCard,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  AlertCircle,
  Mail,
  Phone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CompensationHistoryCard } from "@/components/employees/CompensationHistoryCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { DocumentUpload } from "@/components/ui/document-upload";
import { DocumentList } from "@/components/ui/document-list";
import { useDocuments } from "@/hooks/useDocuments";
import { FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { EMPLOYMENT_TYPE_OPTIONS_SHORT, normalizeEmploymentType, formatEmploymentType } from "@/lib/employmentType";
import { EMPLOYEE_STATUS_OPTIONS, formatStatus, normalizeStatus } from "@/lib/employeeStatus";
import { COMPENSATION_TYPE_OPTIONS, PAY_CYCLE_OPTIONS } from "@/lib/employeeDefaults";
import { formatPhoneInputValue } from "@/lib/phone";
import {
  insertEmployeeSchema,
  type Employee,
  type EmergencyContact,
  type Dependent,
  isHealthOrMedicalPlan,
} from "@shared/schema";
import { AddressFields } from "@/components/forms/AddressFields";
import { EmergencyContactModal } from "@/components/forms/EmergencyContactModal";
import { TerminateEmployeeModal } from "@/components/employee/TerminateEmployeeModal";
import { BenefitsConfigurationForm } from "@/components/benefits/BenefitsConfigurationForm";
import { EnrollmentBadges } from "@/components/ui/benefits/EnrollmentBadges";
import { DeductionSummaryContent } from "@/components/ui/benefits/DeductionSummaryContent";
import { enrollmentEmployeeMonthly, totalPricedMonthly } from "@/lib/planPriceDisplay";
import { CoverageHeaderBanner } from "@/components/benefits/v2/CoverageHeaderBanner";
import { BenefitSection } from "@/components/benefits/v2/BenefitSection";
import { ActiveBenefitRow } from "@/components/benefits/v2/ActiveBenefitRow";
import { WaivedBenefitRow } from "@/components/benefits/v2/WaivedBenefitRow";
import { EmptyTypeRow } from "@/components/benefits/v2/EmptyTypeRow";
import { ModifyTypeModal } from "@/components/benefits/v2/ModifyTypeModal";
import { useTypeSections } from "@/components/benefits/v2/useTypeSections";
import { normalizePlanType, isFsaPlanType } from "@shared/schemas/planTypes";
import { isLifeDisabilityType } from "@shared/schemas/plans";
import { resolveNeedsConfigReason } from "@/components/benefits/v2/needsConfig";
import { isPendingEnrollment } from "@/lib/enrollmentFilters";
import { PermissionButton } from "@/components/ui/permission-button";
import {
  useEmploymentChangeVerification,
  detectEmploymentChanges,
  type VerificationResult,
} from "@/components/ui/EmploymentChangeVerificationModal";
import { composeAddressLine, emptyAddressFields, normalizeAddressFields } from "@/lib/address";
import { format } from "date-fns";
import { formatDateDisplay, parseDateLocal } from "@/lib/utils";
import {
  formatPremiumWithPayCycle,
  getPayCycleLabel,
  getPayCycleLabelCapitalized,
  calculatePerPaycheckAmount,
  hasNonMonthlyPayCycle,
} from "@/lib/payrollUtils";
import { groupEnrollmentsByPlan } from "@/lib/enrollmentGrouping";
import { isCurrentlyEffectiveEnrollment } from "@/lib/enrollmentFilters";
import { redactSsnDeep } from "@shared/utils/ssnRedaction";
import { SsnInput } from "@/components/employees/SsnInput";
import { ssnToSave } from "@/lib/ssnReveal";

// Documents Tab Component
function DocumentsTabContent({ employeeId, clientId, employeeName }: { employeeId: string; clientId?: number; employeeName: string }) {
  const { documents, loading, error, refreshDocuments } = useDocuments({
    ownerType: "employee",
    clientId: clientId,
    employeeId: parseInt(employeeId, 10),
  });
  const { toast } = useToast();

  return (
    <TabsContent value="documents">
      <Card className="border-l-4 border-l-green-500">
        <CardHeader className="bg-green-50 dark:bg-green-950/20">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-green-600" />
              Documents
            </CardTitle>
            <DocumentUpload
              ownerType="employee"
              employeeId={parseInt(employeeId, 10)}
              clientId={clientId}
              onUploadSuccess={async () => {
                await refreshDocuments();
                toast({
                  title: "Document uploaded",
                  description: `Document added for ${employeeName}`,
                  variant: "success",
                });
              }}
              trigger={
                <Button size="sm" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Upload Document
                </Button>
              }
            />
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Loading documents...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-destructive">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Error loading documents</p>
              <p className="text-sm">{error}</p>
            </div>
          ) : (
            <DocumentList
              documents={documents}
              onDocumentUpdate={refreshDocuments}
              showActions={true}
              showStatus={false}
              emptyStateAction={
                <DocumentUpload
                  ownerType="employee"
                  employeeId={parseInt(employeeId, 10)}
                  clientId={clientId}
                  onUploadSuccess={refreshDocuments}
                  trigger={
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Upload First Document
                    </Button>
                  }
                />
              }
            />
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
}

export default function EmployeeDetails() {
  const { id: employeeId } = useParams();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  // Full SSN fetched on demand (API rows carry ***-**-1234); undefined = not revealed.
  const [revealedSsn, setRevealedSsn] = useState<string | null | undefined>(undefined);
  // Same, for the dependent in the Edit Dependent dialog.
  const [revealedDependentSsn, setRevealedDependentSsn] = useState<string | null | undefined>(undefined);
  const [activeTab, setActiveTab] = useState("profile");
  const [showAddEmergencyContact, setShowAddEmergencyContact] = useState(false);
  const [showAddDependent, setShowAddDependent] = useState(false);
  const [showAddDependentBenefitsPrompt, setShowAddDependentBenefitsPrompt] =
    useState(false);
  const [dependentPendingBenefits, setDependentPendingBenefits] =
    useState<any>(null);
  const [editingDependent, setEditingDependent] = useState<any>(null);
  const [editingEmergencyContact, setEditingEmergencyContact] =
    useState<any>(null);
  const [showAddDependentToCoverage, setShowAddDependentToCoverage] =
    useState(false);
  const [showConfigureBenefits, setShowConfigureBenefits] = useState(false);
  // Per-type Modify modal context (mirror of broker page's unified modal).
  const [modifyContext, setModifyContext] = useState<{
    type: string;
    current?: any | null;
    waived?: any | null;
  } | null>(null);
  // Scoped Configure-Benefits wizard. When set, BenefitsConfigurationForm renders only these types.
  const [configureScope, setConfigureScope] = useState<string[] | null>(null);

  const [isAddJobTitleOpen, setIsAddJobTitleOpen] = useState(false);
  const [newJobTitle, setNewJobTitle] = useState("");
  const [isAddDepartmentOpen, setIsAddDepartmentOpen] = useState(false);
  const [newDepartment, setNewDepartment] = useState("");
  const [isAddLocationOpen, setIsAddLocationOpen] = useState(false);
  const [newLocOffice, setNewLocOffice] = useState("");
  const [newLocCity, setNewLocCity] = useState("");
  const [newLocState, setNewLocState] = useState("");
  const [isAddClassOpen, setIsAddClassOpen] = useState(false);
  const [newClass, setNewClass] = useState("");
  const [isAddDivisionOpen, setIsAddDivisionOpen] = useState(false);
  const [newDivision, setNewDivision] = useState("");
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [selectedCoverageForDependent, setSelectedCoverageForDependent] =
    useState<any>(null);
  const [selectedDependentIds, setSelectedDependentIds] = useState<number[]>(
    []
  );
  const [newCoverageLevel, setNewCoverageLevel] = useState<string>("");
  const [waiveReasons, setWaiveReasons] = useState<{
    [dependentId: number]: string;
  }>({});
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const {
    verifyChanges,
    VerificationModal: EmploymentVerificationModal,
    isVerifying,
  } = useEmploymentChangeVerification();

  // Local currency formatter to mirror broker UI formatting
  const formatCurrency = (n: number | string | null | undefined) => {
    const v = typeof n === "number" ? n : parseFloat(String(n ?? 0));
    if (!Number.isFinite(v)) return "$0.00";
    return v.toLocaleString(undefined, { style: "currency", currency: "USD" });
  };

  const { data: employee, isLoading: isLoadingEmployee } = useQuery<Employee>({
    queryKey: [`/api/employees/${employeeId}`],
    enabled: !!employeeId,
  });

  // Org owner context for org reference lists (job titles, departments, etc.)
  // Employer portal should be client-scoped; fall back gracefully if not.
  const orgOwnerType = employee?.clientId ? "client" : employee?.prospectId ? "prospect" : "agency";
  const orgOwnerId =
    orgOwnerType === "client"
      ? employee?.clientId
      : orgOwnerType === "prospect"
      ? employee?.prospectId
      : employee?.agencyId;

  const orgOwnerPayload = useMemo(() => {
    if (!orgOwnerId) return null;
    if (orgOwnerType === "agency") {
      return {
        ownerType: "agency" as const,
        agencyId: String(orgOwnerId),
        clientId: null,
        prospectId: null,
      };
    }
    if (orgOwnerType === "prospect") {
      return {
        ownerType: "prospect" as const,
        agencyId: null,
        clientId: null,
        prospectId: Number(orgOwnerId),
      };
    }
    return {
      ownerType: "client" as const,
      agencyId: null,
      clientId: Number(orgOwnerId),
      prospectId: null,
    };
  }, [orgOwnerId, orgOwnerType]);

  const { data: orgDepartments = [] } = useQuery({
    queryKey: ["/api/org/departments", orgOwnerType, orgOwnerId],
    enabled: !!orgOwnerId,
    queryFn: async () => {
      const res = await fetch(
        `/api/org/departments?ownerType=${orgOwnerType}&ownerId=${orgOwnerId}`
      );
      if (!res.ok) return [] as any[];
      return res.json();
    },
  });

  const { data: payGroups = [] } = useQuery<any[]>({
    queryKey: [(employee as any)?.clientId ? `/api/clients/${(employee as any).clientId}/pay-groups` : ""],
    enabled: !!(employee as any)?.clientId,
  });

  const { data: orgJobTitles = [] } = useQuery({
    queryKey: ["/api/org/job-titles", orgOwnerType, orgOwnerId],
    enabled: !!orgOwnerId,
    queryFn: async () => {
      const res = await fetch(
        `/api/org/job-titles?ownerType=${orgOwnerType}&ownerId=${orgOwnerId}`
      );
      if (!res.ok) return [] as any[];
      return res.json();
    },
  });

  const { data: orgLocations = [] } = useQuery({
    queryKey: ["/api/org/locations", orgOwnerType, orgOwnerId],
    enabled: !!orgOwnerId,
    queryFn: async () => {
      const res = await fetch(
        `/api/org/locations?ownerType=${orgOwnerType}&ownerId=${orgOwnerId}`
      );
      if (!res.ok) return [] as any[];
      return res.json();
    },
  });

  const { data: orgEmployeeClasses = [] } = useQuery({
    queryKey: ["/api/org/employee-classes", orgOwnerType, orgOwnerId],
    enabled: !!orgOwnerId,
    queryFn: async () => {
      const res = await fetch(
        `/api/org/employee-classes?ownerType=${orgOwnerType}&ownerId=${orgOwnerId}`
      );
      if (!res.ok) return [] as any[];
      return res.json();
    },
  });

  const { data: orgDivisions = [] } = useQuery({
    queryKey: ["/api/org/divisions", orgOwnerType, orgOwnerId],
    enabled: !!orgOwnerId,
    queryFn: async () => {
      const res = await fetch(
        `/api/org/divisions?ownerType=${orgOwnerType}&ownerId=${orgOwnerId}`
      );
      if (!res.ok) return [] as any[];
      return res.json();
    },
  });

  // Fetch all employees for manager selection
  const { data: allEmployees = [] } = useQuery<Employee[]>({
    queryKey: ["/api/employees", { clientId: employee?.clientId }],
    enabled: !!employee?.clientId,
    queryFn: async () => {
      const res = await fetch(
        `/api/employees?clientId=${employee?.clientId}`,
        { credentials: "include" }
      );
      if (!res.ok) return [] as Employee[];
      return res.json();
    },
  });

  // Filter potential managers (exclude current employee)
  const potentialManagers = useMemo(() => {
    return allEmployees.filter(
      (emp: Employee) => String(emp.id) !== String(employeeId)
    );
  }, [allEmployees, employeeId]);

  const formatOrgLocation = (loc: any) =>
    [loc?.officeName, loc?.city, loc?.state].filter(Boolean).join(" – ");

  const addJobTitle = useMutation({
    mutationFn: async (name: string) => {
      if (!orgOwnerPayload) throw new Error("Missing org owner context");
      return apiRequest("POST", "/api/org/job-titles", {
        ...orgOwnerPayload,
        name,
        description: null,
      });
    },
    onSuccess: async (res) => {
      const created = await res.json();
      queryClient.invalidateQueries({
        queryKey: ["/api/org/job-titles", orgOwnerType, orgOwnerId],
      });
      employmentForm.setValue("jobTitleId", created?.id ?? null, {
        shouldDirty: true,
      });
      setIsAddJobTitleOpen(false);
      setNewJobTitle("");
      toast({ title: "Job title added", variant: "success" });
    },
    onError: () =>
      toast({ title: "Failed to add job title", variant: "destructive" }),
  });

  const addDepartment = useMutation({
    mutationFn: async (name: string) => {
      if (!orgOwnerPayload) throw new Error("Missing org owner context");
      return apiRequest("POST", "/api/org/departments", {
        ...orgOwnerPayload,
        name,
        description: null,
      });
    },
    onSuccess: async (res) => {
      const created = await res.json();
      queryClient.invalidateQueries({
        queryKey: ["/api/org/departments", orgOwnerType, orgOwnerId],
      });
      employmentForm.setValue("departmentId", created?.id ?? null, {
        shouldDirty: true,
      });
      setIsAddDepartmentOpen(false);
      setNewDepartment("");
      toast({ title: "Department added", variant: "success" });
    },
    onError: () =>
      toast({ title: "Failed to add department", variant: "destructive" }),
  });

  const addLocation = useMutation({
    mutationFn: async (data: { officeName?: string; city?: string; state?: string }) => {
      if (!orgOwnerPayload) throw new Error("Missing org owner context");
      return apiRequest("POST", "/api/org/locations", {
        ...orgOwnerPayload,
        officeName: data.officeName || null,
        city: data.city || null,
        state: data.state || null,
      });
    },
    onSuccess: async (res) => {
      const created = await res.json();
      queryClient.invalidateQueries({
        queryKey: ["/api/org/locations", orgOwnerType, orgOwnerId],
      });
      employmentForm.setValue("locationId", created?.id ?? null, {
        shouldDirty: true,
      });
      setIsAddLocationOpen(false);
      setNewLocOffice("");
      setNewLocCity("");
      setNewLocState("");
      toast({ title: "Location added", variant: "success" });
    },
    onError: () =>
      toast({ title: "Failed to add location", variant: "destructive" }),
  });

  const addEmployeeClass = useMutation({
    mutationFn: async (name: string) => {
      if (!orgOwnerPayload) throw new Error("Missing org owner context");
      return apiRequest("POST", "/api/org/employee-classes", {
        ...orgOwnerPayload,
        name,
        description: null,
      });
    },
    onSuccess: async (res) => {
      const created = await res.json();
      queryClient.invalidateQueries({
        queryKey: ["/api/org/employee-classes", orgOwnerType, orgOwnerId],
      });
      employmentForm.setValue("employeeClassId", created?.id ?? null, {
        shouldDirty: true,
      });
      setIsAddClassOpen(false);
      setNewClass("");
      toast({ title: "Employee class added", variant: "success" });
    },
    onError: () =>
      toast({ title: "Failed to add employee class", variant: "destructive" }),
  });

  const addDivision = useMutation({
    mutationFn: async (name: string) => {
      if (!orgOwnerPayload) throw new Error("Missing org owner context");
      return apiRequest("POST", "/api/org/divisions", {
        ...orgOwnerPayload,
        name,
        description: null,
      });
    },
    onSuccess: async (res) => {
      const created = await res.json();
      queryClient.invalidateQueries({
        queryKey: ["/api/org/divisions", orgOwnerType, orgOwnerId],
      });
      employmentForm.setValue("divisionId", created?.id ?? null, {
        shouldDirty: true,
      });
      setIsAddDivisionOpen(false);
      setNewDivision("");
      toast({ title: "Division added", variant: "success" });
    },
    onError: () =>
      toast({ title: "Failed to add division", variant: "destructive" }),
  });

  const { data: emergencyContacts = [] } = useQuery<EmergencyContact[]>({
    queryKey: [`/api/employees/${employeeId}/emergency-contacts`],
    enabled: !!employeeId,
  });

  const { data: dependents = [] } = useQuery<Dependent[]>({
    queryKey: [`/api/employees/${employeeId}/dependents`],
    enabled: !!employeeId,
  });

  const { data: historyLogs = [] } = useQuery({
    queryKey: [`/api/employees/${employeeId}/history`],
    enabled: !!employeeId,
  });

  const { data: availablePlans = [] } = useQuery({
    queryKey: ["/api/plans"],
  });

  const { data: clientPlans = [], isLoading: isLoadingClientPlans } = useQuery({
    queryKey: ["/api/client-plans", employee?.clientId],
    queryFn: async () => {
      const res = await fetch(`/api/client-plans?clientId=${employee?.clientId}`);
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    enabled: !!employee?.clientId,
  });

  // Plan contributions — used to render the "Needs configuration" pill on
  // benefits cards whose plans have no contribution rows yet.
  const { data: planContributions = [], isFetched: planContributionsFetched } = useQuery({
    queryKey: ["/api/plan-contributions"],
    queryFn: async () => {
      const res = await fetch("/api/plan-contributions");
      if (!res.ok) return [] as any[];
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    staleTime: Infinity,
  });

  const { data: rawEnrollments = [] } = useQuery({
    queryKey: ["/api/enrollments", "employee", employeeId],
    enabled: !!employeeId,
    queryFn: async () => {
      const res = await fetch(`/api/enrollments?employeeId=${employeeId}&computePremiums=1`);
      if (!res.ok) throw new Error("Failed to fetch enrollments");
      const all = await res.json();
      return Array.isArray(all) ? all : [];
    },
  });

  const baseEnrollments = useMemo(
    () => (rawEnrollments as any[]).filter((e: any) => e.dependentId == null),
    [rawEnrollments]
  );

  const enrolledPlansForEditingDependent = useMemo(() => {
    const dependentId = Number((editingDependent as any)?.id);
    if (!Number.isFinite(dependentId)) return [] as { planId: number; planName: string; rows: any[] }[];

    const planNameById = new Map<number, string>();
    for (const p of (availablePlans as any[])) {
      const id = Number((p as any)?.id);
      if (!Number.isFinite(id)) continue;
      planNameById.set(id, String((p as any)?.name || (p as any)?.type || `Plan ${id}`));
    }

    const rows = (rawEnrollments as any[]).filter((r) => Number((r as any)?.dependentId) === dependentId);
    const byPlan = new Map<number, any[]>();
    for (const r of rows) {
      const planId = Number((r as any)?.planId);
      if (!Number.isFinite(planId)) continue;
      const arr = byPlan.get(planId) || [];
      arr.push(r);
      byPlan.set(planId, arr);
    }

    return Array.from(byPlan.entries()).map(([planId, planRows]) => ({
      planId,
      planName: planNameById.get(planId) || `Plan ${planId}`,
      rows: planRows,
    }));
  }, [editingDependent, rawEnrollments, availablePlans]);

  // Normalize to one base enrollment per plan with aggregated dependentIds.
  // NOTE: this strips the per-dependent rows. Components that resolve covered
  // dependents (ActiveBenefitRow, ModifyTypeModal) need the singular-dependentId
  // rows — pass `rawEnrollments` to those, not `enrollments`.
  const enrollments = useMemo(() => {
    const list: any[] = Array.isArray(rawEnrollments) ? rawEnrollments : [];
    const planById = new Map<number, any>();
    for (const p of (availablePlans as any[])) {
      const id = Number((p as any)?.id);
      if (Number.isFinite(id)) planById.set(id, p);
    }

    const grouped = groupEnrollmentsByPlan(list);
    const result: any[] = [];

    for (const [planId, group] of grouped.entries()) {
      const base = group.base || group.waivedRows[0];
      if (!base) continue;
      result.push({
        ...base,
        dependentIds: (base as any)?.dependentIds ?? group.dependentIds,
        plan: (base as any)?.plan ?? planById.get(planId) ?? null,
      });
    }

    return result;
  }, [rawEnrollments, availablePlans]);

  const { activeEnrollments, pendingEnrollments, waivedEnrollments, effectiveEnrollments, totals } = useMemo(() => {
    const asNumber = (v: any): number =>
      typeof v === "number" && isFinite(v) ? v : 0;
    const getPremiums = (enr: any) => {
      const total = asNumber(enr?.totalPremium ?? enr?.premium?.totalMonthlyRate);
      const emp = asNumber(enr?.employeePremium ?? enr?.premium?.employeePremium);
      const er = asNumber(enr?.employerPremium ?? enr?.premium?.employerPremium);
      return { total, emp, er };
    };
    const nonWaived = (enrollments as any[]).filter((e) => {
      const status = String(e?.status || "active").toLowerCase();
      const isWaived = Boolean((e as any)?.waivedCoverage) || status === "waived";
      return !isWaived;
    });
    const pending = nonWaived.filter((e: any) =>
      String(e?.status || "").toLowerCase() === "pending" || isPendingEnrollment(e),
    );
    const pendingIds = new Set(pending.map((e: any) => e.id));
    const active = nonWaived.filter((e: any) => !pendingIds.has(e.id));
    const waived = (enrollments as any[]).filter((e) => {
      const status = String(e?.status || "").toLowerCase();
      const isWaived = Boolean((e as any)?.waivedCoverage) || status === "waived";
      return isWaived;
    });
    // Only currently-effective enrollments count toward deductions
    const effective = active.filter(isCurrentlyEffectiveEnrollment);
    const t = effective.reduce(
      (acc, e) => {
        const p = getPremiums(e);
        acc.total += p.total;
        acc.emp += p.emp;
        acc.er += p.er;
        return acc;
      },
      { total: 0, emp: 0, er: 0 }
    );
    return {
      activeEnrollments: active,
      pendingEnrollments: pending,
      waivedEnrollments: waived,
      effectiveEnrollments: effective,
      totals: t,
    };
  }, [enrollments]);

  // DEV-382: plans the server couldn't price (no rates for the group's rating
  // area) are null lines; the banner and summary withhold the total.
  const deductionItems = (effectiveEnrollments as any[]).map((b: any) => ({
    label: b?.plan?.name || b?.plan?.type || "Benefit",
    monthlyAmount: enrollmentEmployeeMonthly(b),
  }));
  const { unpricedPlans } = totalPricedMonthly(deductionItems);

  // Type-sectioned view derivation — shared with broker page.
  const typeSections = useTypeSections({
    activeBenefits: activeEnrollments as any[],
    pendingBenefits: pendingEnrollments as any[],
    waivedBenefits: waivedEnrollments as any[],
    clientPlans: clientPlans as any[],
  });

  const offeredTypeIds = useMemo(() => {
    const s = new Set<string>();
    for (const cp of (clientPlans as any[]) || []) {
      const t = normalizePlanType(cp?.plan?.type);
      if (t) s.add(t === "health" ? "medical" : t);
    }
    return s;
  }, [clientPlans]);

  const openConfigureBenefits = (scope: string[] | null) => {
    setConfigureScope(scope);
    setShowConfigureBenefits(true);
  };

  const openModifyForType = (planType: string) => {
    const t = normalizePlanType(planType);
    const current = (activeEnrollments as any[])
      .concat(pendingEnrollments as any[])
      .find((b: any) => normalizePlanType(b?.plan?.type) === t);
    const waived = (waivedEnrollments as any[]).find(
      (b: any) => normalizePlanType(b?.plan?.type) === t,
    );
    setModifyContext({ type: t, current, waived });
  };

  const plansForType = (planType: string) => {
    const t = normalizePlanType(planType);
    return (clientPlans as any[]).filter(
      (cp: any) => normalizePlanType(cp?.plan?.type) === t,
    );
  };

  // Check if employee can be deleted (no hire date, no enrollments)
  const { data: canDeleteData } = useQuery<{ canDelete: boolean }>({
    queryKey: [`/api/employees/${employeeId}/can-delete`],
    enabled: !!employeeId,
    queryFn: async () => {
      if (!employeeId) return { canDelete: false };
      const res = await fetch(`/api/employees/${employeeId}/can-delete`, {
        credentials: "include",
      });
      if (!res.ok) return { canDelete: false };
      return res.json();
    },
  });

  // Debug: Log deletion eligibility
  console.log('Employee deletion eligibility:', {
    employeeId,
    canDeleteData,
    hireDate: employee?.hireDate,
    enrollmentCount: enrollments?.length
  });

  const updateEmployeeMutation = useMutation({
    mutationFn: (data: any) =>
      apiRequest("PATCH", `/api/employees/${employeeId}`, data),
    onSuccess: async (updatedEmployee) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}`],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/employees"] });
      toast({ title: "Employee updated successfully" , variant: "success"});
      setIsEditing(false);
      try {
        const profileChanges = (window as any).__profileChanges || {};
        const employmentChanges = (window as any).__employmentChanges || {};
        const allChanges = { ...profileChanges, ...employmentChanges };
        const changeCount = Object.keys(allChanges).length;
        const description = changeCount > 0 
          ? `Updated employee information`
          : "Employee details updated";
        
        await apiRequest("POST", `/api/employees/${employeeId}/history`, {
          action: "employee_updated",
          description,
          recordType: "employee",
          recordId: Number(employeeId),
          changes: allChanges,
        });
        queryClient.invalidateQueries({ queryKey: [`/api/employees/${employeeId}/history`] });
        
        // Clean up temp storage
        delete (window as any).__profileChanges;
        delete (window as any).__employmentChanges;
      } catch (err) {
        console.error("Failed to create employee history:", err);
      }
    },
    onError: (error: any) => {
      toast({ title: error.message || "Failed to update employee", variant: "destructive" });
    },
  });

  const deleteEmployeeMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/employees/${employeeId}`),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["/api/employees"] });
      toast({ title: "Employee deleted successfully" , variant: "success"});
      setLocation("/employees?role=employer");
      try {
        await apiRequest("POST", `/api/employees/${employeeId}/history`, {
          action: "employee_deleted",
          description: "Employee deleted",
          recordType: "employee",
          recordId: Number(employeeId),
        });
        queryClient.invalidateQueries({ queryKey: [`/api/employees/${employeeId}/history`] });
      } catch (err) {
        console.error("Failed to create employee deletion history:", err);
      }
    },
    onError: (error: any) => {
      const message = error?.message || "Failed to delete employee";
      toast({ title: message, variant: "destructive" });
    },
  });

  const terminateMutation = useMutation({
    mutationFn: async (payload: { terminationDate?: string; terminationReason?: string; notes?: string }) => {
      const response = await fetch(`/api/employees/${employeeId}/terminate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload || {}),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to terminate employee");
      }
      return response.json();
    },
    onSuccess: async () => {
      setShowTerminateModal(false);
      toast({
        title: "Employee terminated",
        description: "All enrollments ended.",
        variant: "success"
      });
      // Invalidate employee, enrollments, benefits, dependents
      queryClient.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}`],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/employees"] });
      queryClient.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/benefits`],
      });
      try {
        await apiRequest("POST", `/api/employees/${employeeId}/history`, {
          action: "employment_terminated",
          description: "Employment terminated",
          recordType: "employee",
          recordId: Number(employeeId),
        });
        queryClient.invalidateQueries({ queryKey: [`/api/employees/${employeeId}/history`] });
      } catch (err) {
        console.error("Failed to log termination history:", err);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Termination failed",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const createEmergencyContactMutation = useMutation({
    mutationFn: (data: any) =>
      apiRequest(
        "POST",
        `/api/employees/${employeeId}/emergency-contacts`,
        data
      ),
    onSuccess: async (created: any) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/emergency-contacts`],
      });
      toast({ title: "Emergency contact added successfully" , variant: "success"});
      setShowAddEmergencyContact(false);
      setEditingEmergencyContact(null);
      try {
        const contactName = created?.name || (window as any).__emergencyContactAddName || "New Contact";
        
        await apiRequest("POST", `/api/employees/${employeeId}/history`, {
          action: "emergency_contact_added",
          description: `Added emergency contact: ${contactName}`,
          recordType: "emergency_contact",
          recordId: created?.id ?? null,
        });
        queryClient.invalidateQueries({ queryKey: [`/api/employees/${employeeId}/history`] });
        delete (window as any).__emergencyContactAddName;
      } catch (err) {
        console.error("Failed to create emergency contact history:", err);
      }
    },
    onError: () => {
      toast({
        title: "Failed to add emergency contact",
        variant: "destructive",
      });
    },
  });

  const deleteEmergencyContactMutation = useMutation({
    mutationFn: (contactId: number) =>
      apiRequest("DELETE", `/api/emergency-contacts/${contactId}`),
    onSuccess: async (_data, contactId) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/emergency-contacts`],
      });
      toast({ title: "Emergency contact deleted successfully" , variant: "success"});
      try {
        const contactName = (window as any).__emergencyContactDeleteName || "Emergency Contact";
        await apiRequest("POST", `/api/employees/${employeeId}/history`, {
          action: "emergency_contact_deleted",
          description: `Deleted emergency contact: ${contactName}`,
          recordType: "emergency_contact",
          recordId: contactId ?? null,
        });
        queryClient.invalidateQueries({ queryKey: [`/api/employees/${employeeId}/history`] });
        delete (window as any).__emergencyContactDeleteName;
      } catch (err) {
        console.error("Failed to create emergency contact deletion history:", err);
      }
    },
    onError: () => {
      toast({
        title: "Failed to delete emergency contact",
        variant: "destructive",
      });
    },
  });

  const updateEmergencyContactMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      apiRequest("PATCH", `/api/emergency-contacts/${id}`, data),
    onSuccess: async (updated: any) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/emergency-contacts`],
      });
      toast({ title: "Emergency contact updated successfully" , variant: "success"});
      setShowAddEmergencyContact(false);
      setEditingEmergencyContact(null);
      try {
        const changes = (window as any).__emergencyContactChanges || {};
        const contactName = (window as any).__emergencyContactUpdateName || updated?.name || "Emergency Contact";
        const description = `Updated emergency contact: ${contactName}`;
        
        await apiRequest("POST", `/api/employees/${employeeId}/history`, {
          action: "emergency_contact_updated",
          description,
          recordType: "emergency_contact",
          recordId: updated?.id ?? null,
          changes,
        });
        queryClient.invalidateQueries({ queryKey: [`/api/employees/${employeeId}/history`] });
        delete (window as any).__emergencyContactChanges;
        delete (window as any).__emergencyContactUpdateName;
      } catch (err) {
        console.error("Failed to create emergency contact update history:", err);
      }
    },
    onError: () => {
      toast({
        title: "Failed to update emergency contact",
        variant: "destructive",
      });
    },
  });

  const createDependentMutation = useMutation({
    mutationFn: (data: any) =>
      apiRequest("POST", `/api/employees/${employeeId}/dependents`, data),
    onSuccess: async (created: any) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/dependents`],
      });
      toast({ title: "Dependent added successfully" , variant: "success"});
      setShowAddDependent(false);
      dependentForm.reset();
      try {
        const dependentName = `${created?.firstName || ""} ${created?.lastName || ""}`.trim() || "New Dependent";
        
        await apiRequest("POST", `/api/employees/${employeeId}/history`, {
          action: "dependent_added",
          description: `Added dependent: ${dependentName}`,
          recordType: "dependent",
          recordId: created?.id ?? null,
        });
        queryClient.invalidateQueries({ queryKey: [`/api/employees/${employeeId}/history`] });
      } catch (err) {
        console.error("Failed to create dependent history:", err);
      }
      // Prompt employer to optionally add the new dependent to benefits right away.
      setDependentPendingBenefits(created);
      setShowAddDependentBenefitsPrompt(true);
    },
    onError: () => {
      toast({ title: "Failed to add dependent", variant: "destructive" });
    },
  });

  const updateDependentMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      apiRequest("PATCH", `/api/dependents/${id}`, data),
    onSuccess: async (updated: any) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/dependents`],
      });
      toast({ title: "Dependent updated successfully" , variant: "success"});
      setEditingDependent(null);
      try {
        const changes = (window as any).__dependentChanges || {};
        const dependentName = (window as any).__dependentName || `${updated?.firstName || ""} ${updated?.lastName || ""}`;
        const description = `Updated dependent: ${dependentName}`;
        
        await apiRequest("POST", `/api/employees/${employeeId}/history`, {
          action: "dependent_updated",
          description,
          recordType: "dependent",
          recordId: updated?.id ?? null,
          changes,
        });
        queryClient.invalidateQueries({ queryKey: [`/api/employees/${employeeId}/history`] });
        delete (window as any).__dependentChanges;
        delete (window as any).__dependentName;
      } catch (err) {
        console.error("Failed to create dependent update history:", err);
      }
    },
    onError: () => {
      toast({ title: "Failed to update dependent", variant: "destructive" });
    },
  });

  const deleteDependentMutation = useMutation({
    mutationFn: (dependentId: number) =>
      apiRequest("DELETE", `/api/dependents/${dependentId}`),
    onSuccess: async (_data, dependentId) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/dependents`],
      });
      toast({ title: "Dependent deleted successfully" , variant: "success"});
      // Server already creates history entry for dependent deletion
      queryClient.invalidateQueries({ queryKey: [`/api/employees/${employeeId}/history`] });
    },
    onError: () => {
      toast({ title: "Failed to delete dependent", variant: "destructive" });
    },
  });

  const addDependentToCoverageMutation = useMutation({
    mutationFn: async (data: any) => {
      const employeeNumericId = parseInt(String(employeeId || "0"), 10);
      const coverageId = Number(data?.coverageId);
      const planId = Number(data?.planId ?? selectedCoverageForDependent?.planId);
      const clientId = Number((employee as any)?.clientId);
      const dependentIds = Array.isArray(data?.dependentIds)
        ? (data.dependentIds as any[])
            .map((n) => Number(n))
            .filter((n) => Number.isFinite(n))
        : [];
      const coverageTier = String(
        data?.coverageTier ??
          selectedCoverageForDependent?.coverageTier ??
          "employee_only"
      );
      const effectiveDate = String(
        data?.effectiveDate ||
          selectedCoverageForDependent?.effectiveDate ||
          new Date().toISOString().split("T")[0]
      );

      if (!Number.isFinite(employeeNumericId) || employeeNumericId <= 0) {
        throw new Error("Missing employee ID");
      }
      if (!Number.isFinite(planId) || planId <= 0) {
        throw new Error("Missing plan ID");
      }
      if (!Number.isFinite(clientId) || clientId <= 0) {
        throw new Error("Missing client ID");
      }
      if (dependentIds.length === 0) {
        throw new Error("At least one dependent must be selected");
      }

      // Read current enrollments to avoid creating duplicate dependent rows.
      const existingRes = await fetch(
        `/api/enrollments?employeeId=${employeeNumericId}`,
        {
          credentials: "include",
        }
      );
      if (!existingRes.ok) {
        const msg = (await existingRes.json().catch(() => ({})))?.message;
        throw new Error(msg || "Failed to load existing enrollments");
      }
      const existingRows = await existingRes.json();
      const currentForPlan = (existingRows as any[]).filter(
        (r) => r?.planId === planId && Number.isFinite(r?.dependentId)
      );
      const existingSet = new Set<number>(
        currentForPlan
          .map((r) => Number(r?.dependentId))
          .filter((n) => Number.isFinite(n))
      );
      const toAdd = dependentIds.filter((id) => !existingSet.has(id));

      await Promise.all(
        toAdd.map(async (depId) => {
          const res = await fetch(`/api/enrollments`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              employeeId: employeeNumericId,
              clientId,
              planId,
              dependentId: depId,
              coverageTier,
              effectiveDate,
              enrollmentSource: "update",
              status: "active",
              waivedCoverage: false,
            }),
          });
          if (!res.ok) {
            const msg = (await res.json().catch(() => ({})))?.message;
            throw new Error(msg || "Failed to add dependent enrollment");
          }
        })
      );

      // Keep the base enrollment's tier aligned with the dependent addition so the UI tier label updates.
      if (Number.isFinite(coverageId) && coverageId > 0) {
        const res = await fetch(`/api/enrollments/${coverageId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            coverageTier,
          }),
        });
        if (!res.ok) {
          const msg = (await res.json().catch(() => ({})))?.message;
          throw new Error(msg || "Failed to update coverage tier");
        }
      }

      return { coverageId, planId, addedDependentIds: toAdd };
    },
    onSuccess: async (_data, variables: any) => {
      // Employer Benefits UI is driven by enrollments query.
      queryClient.invalidateQueries({
        queryKey: ["/api/enrollments", "employee", employeeId],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/enrollments"] });

      toast({ title: "Dependent added to coverage successfully" , variant: "success"});
      setShowAddDependentToCoverage(false);
      setSelectedCoverageForDependent(null);
      setSelectedDependentIds([]);
      try {
        await apiRequest("POST", `/api/employees/${employeeId}/history`, {
          action: "benefit_dependent_added",
          description: `Added dependent(s) to coverage`,
          recordType: "benefit",
          recordId: variables?.coverageId ?? null,
          changes: variables ?? null,
        });
        queryClient.invalidateQueries({ queryKey: [`/api/employees/${employeeId}/history`] });
      } catch (err) {
        console.error("Failed to create add-dependent-to-coverage history:", err);
      }
    },
    onError: () => {
      toast({
        title: "Failed to add dependent to coverage",
        variant: "destructive",
      });
    },
  });

  const removeDependentFromCoverageMutation = useMutation({
    mutationFn: async ({ dependentId, planId }: { dependentId: number; planId: number }) => {
      const employeeNumericId = parseInt(String(employeeId || "0"), 10);
      if (!Number.isFinite(employeeNumericId) || employeeNumericId <= 0) {
        throw new Error("Missing employee ID");
      }
      const rows = (rawEnrollments as any[]).filter(
        (r) => Number((r as any)?.planId) === planId && Number((r as any)?.dependentId) === dependentId
      );
      const ids = rows
        .map((r) => Number((r as any)?.id))
        .filter((n) => Number.isFinite(n));

      await Promise.all(
        ids.map(async (id) => {
          const res = await fetch(`/api/enrollments/${id}`, {
            method: "DELETE",
            credentials: "include",
          });
          if (res.status === 404) return;
          if (!res.ok) {
            const msg = (await res.json().catch(() => ({})))?.message;
            throw new Error(msg || "Failed to remove dependent enrollment");
          }
        })
      );

      return { planId, removedEnrollmentIds: ids };
    },
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/enrollments", "employee", employeeId],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/enrollments"] });
      toast({ title: "Dependent removed from coverage", variant: "success" });

      // Optional: log history
      try {
        await apiRequest("POST", `/api/employees/${employeeId}/history`, {
          action: "benefit_dependent_removed",
          description: "Removed dependent from coverage",
          recordType: "benefit",
          recordId: null,
          changes: variables ?? null,
        });
        queryClient.invalidateQueries({ queryKey: [`/api/employees/${employeeId}/history`] });
      } catch {}
    },
    onError: () => {
      toast({ title: "Failed to remove dependent from coverage", variant: "destructive" });
    },
  });

  const profileForm = useForm({
    resolver: zodResolver(
      insertEmployeeSchema.pick({
        firstName: true,
        lastName: true,
        email: true,
        phone: true,
        dateOfBirth: true,
        sex: true,
        maritalStatus: true,
        ssn: true,
        tobaccoUser: true,
        disabled: true,
        address1: true,
        address2: true,
        city: true,
        state: true,
        zipCode: true,
        county: true,
        country: true,
      })
    ),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dateOfBirth: "",
      sex: "",
      maritalStatus: "",
      ssn: "",
      tobaccoUser: "",
      disabled: "",
      ...emptyAddressFields(),
    },
  });

  const employmentForm = useForm({
    resolver: zodResolver(
      z.object({
        position: z.string().optional().nullable(),
        department: z.string().optional().nullable(),
        departmentId: z.number().optional().nullable(),
        jobTitleId: z.number().optional().nullable(),
        locationId: z.number().optional().nullable(),
        employeeClassId: z.number().optional().nullable(),
        divisionId: z.number().optional().nullable(),
        hireDate: z.string().optional().nullable(),
        employmentType: z.string().min(1, "Employment type is required"),
        employeeId: z.string().optional().nullable(),
        scheduledHours: z.string().optional().nullable(),
        compensationType: z.string().optional().nullable(),
        compensationAmount: z.string().optional().nullable(),
        payCycle: z.string().optional().nullable(),
        payGroupId: z.number().optional().nullable(),
        status: z.string().optional().nullable(),
        managerEmployeeId: z.number().optional().nullable(),
      })
    ),
    defaultValues: {
      position: employee?.position || "",
      department: employee?.department || "",
      departmentId: (employee as any)?.departmentId ?? null,
      jobTitleId: (employee as any)?.jobTitleId ?? null,
      locationId: (employee as any)?.locationId ?? null,
      employeeClassId: (employee as any)?.employeeClassId ?? null,
      divisionId: (employee as any)?.divisionId ?? null,
      hireDate: (() => {
        const d = parseDateLocal(employee?.hireDate);
        return d ? format(d, "yyyy-MM-dd") : "";
      })(),
      employmentType: employee?.employmentType || "",
      employeeId: employee?.employeeId || "",
      scheduledHours: employee?.scheduledHours || "",
      compensationType: employee?.compensationType || "",
      compensationAmount: employee?.compensationAmount || "",
      payCycle: employee?.payCycle || "",
      payGroupId: (employee as any)?.payGroupId ?? null,
      status: employee?.status || "Active",
      managerEmployeeId: (employee as any)?.managerEmployeeId ?? null,
    },
  });

  const dependentForm = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      relationship: "",
      dateOfBirth: "",
      ssn: "",
      sex: "",
    },
  });

  // Handler functions - must be defined before early returns to avoid hooks order violations
  const handleProfileSubmit = (data: any) => {
    const addressLine = composeAddressLine(data);
    const payload = {
      ...data,
      address: addressLine || data.address || "",
      address1: data.address1 || null,
      address2: data.address2 || null,
      city: data.city || null,
      state: data.state || null,
      zipCode: data.zipCode || null,
      county: data.county || null,
      country: data.country || null,
    };
    // Unchanged (masked or same as revealed) SSN is not sent, so it can't overwrite the stored one.
    if (ssnToSave(payload.ssn, revealedSsn) === undefined) delete payload.ssn;

    // Compute field-level diffs for history
    const changes: Record<string, { from: any; to: any }> = {};
    const fieldsToTrack = [
      'firstName', 'lastName', 'email', 'phone', 'dateOfBirth',
      'sex', 'maritalStatus', 'ssn', 'tobaccoUser', 'disabled',
      'address1', 'address2', 'city', 'state', 'zipCode', 'county', 'country'
    ];
    fieldsToTrack.forEach((field) => {
      if (field === 'ssn' && !('ssn' in payload)) return;
      const oldVal = (employee as any)?.[field];
      const newVal = payload[field];
      if (oldVal !== newVal && (oldVal || newVal)) {
        changes[field] = { from: oldVal ?? 'N/A', to: newVal ?? 'N/A' };
      }
    });
    (window as any).__profileChanges = changes;

    // Validate minimum working age if both dates are available
    if (data.dateOfBirth && employee?.hireDate) {
      // Import validation function dynamically
      import("@/../../shared/utils/ageValidation").then(({ validateHireAge, validateHireDateOrder }) => {
        // Check if hire date is after date of birth
        const dateOrderResult = validateHireDateOrder(data.dateOfBirth, employee.hireDate);
        if (!dateOrderResult.isValid) {
          toast({
            title: "Invalid Date of Birth",
            description: dateOrderResult.message,
            variant: "destructive",
          });
          return;
        }

        // Check minimum working age
        const result = validateHireAge(data.dateOfBirth, employee.hireDate);
        if (!result.isValid) {
          toast({
            title: "Underage Employee Warning",
            description: result.message,
            variant: "destructive",
          });
          return;
        }

        // If validation passes, proceed with mutation
        updateEmployeeMutation.mutate(payload);
      });
    } else {
      // If dates not available, proceed without validation
      updateEmployeeMutation.mutate(payload);
    }
  };

  // Without this, a validation failure (e.g. blank employmentType) silently blocks Save.
  const handleEmploymentInvalid = (errors: Record<string, { message?: string }>) => {
    const firstMessage = Object.values(errors)[0]?.message;
    toast({
      title: "Can't save",
      description: firstMessage || "Please fix the highlighted field before saving.",
      variant: "destructive",
    });
  };

  const handleEmploymentSubmit = async (data: any) => {
    const selectedJobTitle = data.jobTitleId
      ? (orgJobTitles || []).find((jt: any) => jt.id === data.jobTitleId)
      : null;
    const selectedDepartment = data.departmentId
      ? (orgDepartments || []).find((d: any) => d.id === data.departmentId)
      : null;

    // Map selected org IDs while preserving legacy text fields
    const payload = {
      ...data,
      departmentId: data.departmentId ?? null,
      jobTitleId: data.jobTitleId ?? null,
      locationId: data.locationId ?? null,
      employeeClassId: data.employeeClassId ?? null,
      divisionId: data.divisionId ?? null,
      managerEmployeeId: data.managerEmployeeId ?? null,
      position: selectedJobTitle?.name || data.position || null,
      department: selectedDepartment?.name || data.department || null,
    };

    // Detect employment changes that require verification
    const employeeName = employee ? `${employee.firstName} ${employee.lastName}` : undefined;
    const currentEnrollments = enrollments?.filter((e: any) => 
      e.status === 'active' || e.status === 'Active'
    ) || [];
    
    const significantChanges = detectEmploymentChanges(
      employee,
      payload,
      employeeName,
      currentEnrollments
    );
    
    // If there are significant changes, require verification
    if (significantChanges.length > 0) {
      const results = await verifyChanges(significantChanges);
      
      // If user cancelled, don't proceed
      if (!results) {
        return;
      }
      
      // Store verification results with the payload for audit trail
      (payload as any).__verificationResults = results;
    }

    // Compute field-level diffs for history
    const changes: Record<string, { from: any; to: any }> = {};
    const fieldsToTrack = [
      'position',
      'department',
      'jobTitleId',
      'departmentId',
      'locationId',
      'employeeClassId',
      'divisionId',
      'managerEmployeeId',
      'hireDate',
      'employmentType',
      'employeeId',
      'scheduledHours',
      'compensationType',
      'compensationAmount',
      'payCycle',
      'payGroupId',
      'status'
    ];
    fieldsToTrack.forEach((field) => {
      const oldVal = (employee as any)?.[field];
      const newVal = payload[field];
      if (oldVal !== newVal && (oldVal || newVal)) {
        changes[field] = { from: oldVal ?? 'N/A', to: newVal ?? 'N/A' };
      }
    });
    (window as any).__employmentChanges = changes;

    // Validate minimum working age if both dates are available
    if (data.hireDate && employee?.dateOfBirth) {
      // Import validation function dynamically
      import("@/../../shared/utils/ageValidation").then(({ validateHireAge, validateHireDateOrder }) => {
        // Check if hire date is after date of birth
        const dateOrderResult = validateHireDateOrder(employee.dateOfBirth, data.hireDate);
        if (!dateOrderResult.isValid) {
          toast({
            title: "Invalid Hire Date",
            description: dateOrderResult.message,
            variant: "destructive",
          });
          return;
        }

        // Check minimum working age
        const result = validateHireAge(employee.dateOfBirth, data.hireDate);
        if (!result.isValid) {
          toast({
            title: "Underage Employee Warning",
            description: result.message,
            variant: "destructive",
          });
          return;
        }

        // If validation passes, proceed with mutation
        updateEmployeeMutation.mutate(payload);
      });
    } else {
      // If dates not available, proceed without validation
      updateEmployeeMutation.mutate(payload);
    }
  };

  const handleAddEmergencyContact = (data: any) => {
    // Combine firstName and lastName into a single name field for the database
    const contactData = {
      ...data,
      name: `${data.firstName} ${data.lastName}`.trim(),
    };
    // Remove firstName and lastName as they're not expected by the API
    delete contactData.firstName;
    delete contactData.lastName;

    const addressLine = composeAddressLine(data);
    const payload = {
      ...contactData,
      address: addressLine || data.address || "",
      address1: data.address1 || null,
      address2: data.address2 || null,
      city: data.city || null,
      state: data.state || null,
      zipCode: data.zipCode || null,
      county: data.county || null,
      country: data.country || null,
    };

    // Store name for history
    (window as any).__emergencyContactAddName = contactData.name;
    createEmergencyContactMutation.mutate(payload);
  };

  const handleDeleteEmergencyContact = (contactId: number) => {
    // Store contact name for history
    const contact = emergencyContacts.find((c: any) => c.id === contactId);
    if (contact) {
      (window as any).__emergencyContactDeleteName = contact.name;
    }
    deleteEmergencyContactMutation.mutate(contactId);
  };

  const handleEditEmergencyContact = (contact: any) => {
    setEditingEmergencyContact(contact);
    setShowAddEmergencyContact(true);
  };

  const handleUpdateEmergencyContact = (data: any) => {
    if (editingEmergencyContact) {
      // Combine firstName and lastName into a single name field for the database
      const contactData = {
        ...data,
        name: `${data.firstName} ${data.lastName}`.trim(),
      };
      // Remove firstName and lastName as they're not expected by the API
      delete contactData.firstName;
      delete contactData.lastName;

      const addressLine = composeAddressLine(data);
      const payload = {
        ...contactData,
        address: addressLine || data.address || "",
        address1: data.address1 || null,
        address2: data.address2 || null,
        city: data.city || null,
        state: data.state || null,
        zipCode: data.zipCode || null,
        county: data.county || null,
        country: data.country || null,
      };

      // Compute diffs
      const changes: Record<string, { from: any; to: any }> = {};
      const fieldsToTrack = ['name', 'phone', 'relationship', 'address1', 'address2', 'city', 'state', 'zipCode'];
      fieldsToTrack.forEach((field) => {
        const oldVal = editingEmergencyContact[field];
        const newVal = payload[field];
        if (oldVal !== newVal && (oldVal || newVal)) {
          changes[field] = { from: oldVal ?? 'N/A', to: newVal ?? 'N/A' };
        }
      });
      (window as any).__emergencyContactChanges = changes;
      (window as any).__emergencyContactUpdateName = contactData.name;

      updateEmergencyContactMutation.mutate({
        id: editingEmergencyContact.id,
        data: payload,
      });
    }
  };

  // Unified handler for EmergencyContactModal
  const handleEmergencyContactSubmit = (data: any) => {
    if (editingEmergencyContact) {
      // Update existing emergency contact
      const changes: Record<string, { from: any; to: any }> = {};
      const fieldsToTrack = [
        'name', 'phone', 'email', 'relationship', 'contactType',
        'address1', 'address2', 'city', 'state', 'zipCode', 'county', 'country'
      ];
      fieldsToTrack.forEach((field) => {
        const oldVal = editingEmergencyContact[field];
        const newVal = data[field];
        if (oldVal !== newVal && (oldVal || newVal)) {
          changes[field] = { from: oldVal ?? 'N/A', to: newVal ?? 'N/A' };
        }
      });
      (window as any).__emergencyContactChanges = changes;
      (window as any).__emergencyContactUpdateName = data.name;

      updateEmergencyContactMutation.mutate({
        id: editingEmergencyContact.id,
        data: data,
      });
    } else {
      // Create new emergency contact
      (window as any).__emergencyContactAddName = data.name;
      createEmergencyContactMutation.mutate(data);
    }
  };

  const handleAddDependent = (data: any) => {
    createDependentMutation.mutate(data);
  };

  const handleEditDependent = (dependent: any) => {
    setEditingDependent(dependent);
    setRevealedDependentSsn(undefined);
    dependentForm.reset({
      firstName: dependent.firstName,
      lastName: dependent.lastName,
      relationship: dependent.relationship,
      dateOfBirth: (() => {
        const d = parseDateLocal(dependent.dateOfBirth);
        return d ? format(d, "yyyy-MM-dd") : "";
      })(),
      ssn: dependent.ssn || "",
      sex: dependent.sex || "",
    });
  };

  const handleUpdateDependent = (data: any) => {
    if (editingDependent) {
      // Unchanged (masked or same as revealed) SSN is not sent, so it can't overwrite the stored one.
      data = { ...data };
      if (ssnToSave(data.ssn, revealedDependentSsn) === undefined) delete data.ssn;
      // Compute diffs
      const changes: Record<string, { from: any; to: any }> = {};
      const fieldsToTrack = ['firstName', 'lastName', 'relationship', 'dateOfBirth', 'sex', 'ssn'];
      fieldsToTrack.forEach((field) => {
        if (field === 'ssn' && !('ssn' in data)) return;
        let oldVal = editingDependent[field];
        let newVal = data[field];
        
        // Normalize date format for comparison
        if (field === 'dateOfBirth' && oldVal && newVal) {
          const fmt = (v: unknown) => { const d = parseDateLocal(v); return d ? format(d, "yyyy-MM-dd") : v; };
          oldVal = fmt(oldVal);
          newVal = fmt(newVal);
        }
        
        if (oldVal !== newVal && (oldVal || newVal)) {
          changes[field] = { from: oldVal ?? 'N/A', to: newVal ?? 'N/A' };
        }
      });
      (window as any).__dependentChanges = changes;
      (window as any).__dependentName = `${data.firstName || editingDependent.firstName || ''} ${data.lastName || editingDependent.lastName || ''}`.trim();
      
      updateDependentMutation.mutate({ id: editingDependent.id, data });
    }
  };

  const handleDeleteDependent = (dependentId: number) => {
    deleteDependentMutation.mutate(dependentId);
  };

  const handleTerminate = () => {
    if (!employee) return;
    setShowTerminateModal(true);
  };

  const handleTerminationConfirm = (data: { terminationDate: string; terminationReason: string; notes?: string }) => {
    terminateMutation.mutate(data);
  };

  // Re-hire mutation for terminated employees
  const rehireMutation = useMutation({
    mutationFn: async (payload: { rehireDate?: string; notes?: string }) => {
      if (!employeeId) throw new Error("Missing employeeId");
      const response = await fetch(`/api/employees/${employeeId}/rehire`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload || {}),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to re-hire employee");
      }
      return response.json();
    },
    onSuccess: async () => {
      toast({
        title: "Employee re-hired",
        description: "Employee status updated to active.",
        variant: "success"
      });
      // Invalidate employee queries
      queryClient.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}`],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/employees"] });
      queryClient.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/benefits`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/history`],
      });
    },
    onError: (error: any) => {
      const message = error?.message || "Failed to re-hire employee";
      toast({ title: message, variant: "destructive" });
    },
  });

  const handleRehire = () => {
    if (!employee) return;
    const today = new Date().toISOString().split("T")[0];
    if (window.confirm(`Re-hire ${employee.firstName} ${employee.lastName}? This will set their status to active with today's date as the new hire date.`)) {
      rehireMutation.mutate({ rehireDate: today });
    }
  };

  // Prepare active enrollments data for termination modal with termination timing
  const activeEnrollmentsForTermination = useMemo(() => {
    if (!activeEnrollments) return [];
    return activeEnrollments.map((e: any) => ({
      id: e.id,
      planName: e.planName || e.plan?.name || "Unknown Plan",
      planType: e.planType || e.plan?.planType || "health",
      coverageTier: e.coverageTier || e.tier || "Employee Only",
      terminationTiming: e.plan?.terminationTiming || "end_of_month",
      employeePremium: e.employeePremium || e.premium?.employeePremium,
      totalPremium: e.totalPremium || e.premium?.totalPremium,
    }));
  }, [activeEnrollments]);

  // Reset forms when employee data loads (only when not editing to prevent overwriting user changes)
  useEffect(() => {
    if (employee && !isEditing) {
      setRevealedSsn(undefined);
      const addressDefaults = normalizeAddressFields(employee as any);
      profileForm.reset({
        firstName: employee.firstName || "",
        lastName: employee.lastName || "",
        email: employee.email || "",
        phone: employee.phone || "",
        dateOfBirth: (() => {
          const d = parseDateLocal(employee.dateOfBirth);
          return d ? format(d, "yyyy-MM-dd") : "";
        })(),
        // Option values are lowercase; older rows were saved capitalized.
        sex: (employee.sex || "").trim().toLowerCase(),
        maritalStatus: ((employee as any).marital_status || employee.maritalStatus || "").trim().toLowerCase(),
        ssn: employee.ssn || "",
        tobaccoUser: (employee as any).tobacco_user || employee.tobaccoUser || "",
        disabled: (employee as any).disabled || employee.disabled || "",
        ...addressDefaults,
      });
      const normalizedEmploymentType =
        normalizeEmploymentType(employee.employmentType) ||
        employee.employmentType ||
        "";
      employmentForm.reset({
        position: employee.position || "",
        department: employee.department || "",
        departmentId: (employee as any)?.departmentId ?? null,
        jobTitleId: (employee as any)?.jobTitleId ?? null,
        locationId: (employee as any)?.locationId ?? null,
        employeeClassId: (employee as any)?.employeeClassId ?? null,
        divisionId: (employee as any)?.divisionId ?? null,
        hireDate: (() => {
          const d = parseDateLocal(employee.hireDate);
          return d ? format(d, "yyyy-MM-dd") : "";
        })(),
        employmentType: normalizedEmploymentType,
        employeeId: employee.employeeId || "",
        scheduledHours: employee.scheduledHours || "",
        compensationType: employee.compensationType || "",
        compensationAmount:
          employee.compensationAmount || employee.salary || "",
        payCycle: employee.payCycle || "",
        payGroupId: (employee as any)?.payGroupId ?? null,
        status: normalizeStatus(employee.status) || employee.status || "active",
        managerEmployeeId: (employee as any)?.managerEmployeeId ?? null,
      });
    }
  }, [employee, isEditing, profileForm, employmentForm]);

  const profileAddressDefaults = employee
    ? normalizeAddressFields(employee as any)
    : emptyAddressFields();
  const hasProfileAddress = Boolean(
    employee &&
    (employee.address1 ||
      employee.address2 ||
      employee.city ||
      employee.state ||
      employee.zipCode ||
      employee.county ||
      employee.country)
  );
  const profileAddressLine = composeAddressLine(profileAddressDefaults);
  const profileAddressDisplayParts = hasProfileAddress
    ? [profileAddressLine, employee?.county, employee?.country].filter(
        (value): value is string =>
          typeof value === "string" && value.trim().length > 0
      )
    : [];

  const handleAddDependentToCoverage = (coverage: any) => {
    setSelectedCoverageForDependent(coverage);
    setSelectedDependentIds([]);
    setNewCoverageLevel(coverage.coverageLevel || "Employee Only");
    setShowAddDependentToCoverage(true);
  };

  const closeAddDependentBenefitsPrompt = () => {
    setShowAddDependentBenefitsPrompt(false);
    setDependentPendingBenefits(null);
  };

  const confirmAddDependentToBenefits = () => {
    const created = dependentPendingBenefits;
    closeAddDependentBenefitsPrompt();

    setActiveTab("benefits");

    const active = Array.isArray(activeEnrollments)
      ? (activeEnrollments as any[])
      : [];
    if (active.length === 0) {
      toast({
        title: "No active benefit enrollments",
        description:
          "Add a benefit enrollment first, then add dependents to coverage.",
      });
      return;
    }

    const preferredCoverage =
      active.find((e: any) => {
        const planType = String(e?.plan?.type || e?.planType || "").toLowerCase();
        return isHealthOrMedicalPlan(planType);
      }) || active[0];

    handleAddDependentToCoverage(preferredCoverage);
    if (Number.isFinite(created?.id)) {
      setSelectedDependentIds([Number(created.id)]);
    }
  };

  const handleDependentSelection = (dependentId: number, checked: boolean) => {
    if (checked) {
      setSelectedDependentIds((prev) => [...prev, dependentId]);
    } else {
      setSelectedDependentIds((prev) =>
        prev.filter((id) => id !== dependentId)
      );
    }
  };

  // Early returns must come after all hooks
  if (isLoadingEmployee) {
    return (
      <div className="flex items-center justify-center h-64">Loading...</div>
    );
  }

  if (!employee) {
    return (
      <div className="flex items-center justify-center h-64">
        Employee not found
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header with Breadcrumb */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              localStorage.setItem("userRole", "employer");
              setLocation("/employees?role=employer");
            }}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Employees
          </Button>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white dark:bg-card shadow rounded-lg border dark:border-border">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <div className="h-16 w-16 rounded-lg bg-blue-100 dark:bg-blue-950/50 flex items-center justify-center border border-blue-200/50 dark:border-blue-800/50">
                  <User className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <div>
                <div className="flex items-center space-x-3">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {employee.firstName} {employee.lastName}
                  </h1>
                  <Badge
                    variant={
                      employee.status === "active" ? "default" : "secondary"
                    }
                  >
                    {employee.status}
                  </Badge>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mt-1">
                  {employee.position} • {employee.department}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Employee ID: {employee.employeeId || "Not assigned"}
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              {!isEditing && (
                <>
                  {employee?.status?.toLowerCase() !== "terminated" && (
                    <Button
                      onClick={handleTerminate}
                      variant="destructive"
                      className="gap-2"
                    >
                      Terminate
                    </Button>
                  )}
                  {employee?.status?.toLowerCase() === "terminated" && (
                    <Button
                      onClick={handleRehire}
                      variant="default"
                      className="gap-2 bg-green-600 hover:bg-green-700"
                      disabled={rehireMutation.isPending}
                    >
                      {rehireMutation.isPending ? "Re-hiring..." : "Re-Hire"}
                    </Button>
                  )}
                  {canDeleteData?.canDelete && (
                    <Button
                      onClick={() => setShowDeleteConfirmation(true)}
                      variant="destructive"
                      className="gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete Employee
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6 mt-6"
      >
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="employment">Employment</TabsTrigger>
          <TabsTrigger value="benefits">Benefits</TabsTrigger>
          <TabsTrigger value="dependents">Dependents</TabsTrigger>
          <TabsTrigger value="aca">ACA</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="border-l-4 border-l-blue-500 dark:border-l-blue-400">
            <CardHeader className="bg-blue-50 dark:bg-blue-950/30 flex flex-row items-center justify-between space-y-0 pb-4 border-b border-transparent dark:border-blue-900/50">
              <CardTitle className="flex items-center gap-2 text-foreground">
                <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                Personal Information
              </CardTitle>
              {!isEditing ? (
                <Button
                  size="sm"
                  className="gap-2"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit3 className="h-4 w-4" />
                  Edit Profile
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={() => setIsEditing(false)}
                    variant="outline"
                    size="sm"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => profileForm.handleSubmit(handleProfileSubmit)()}
                  >
                    <Save className="h-4 w-4" />
                    Save
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-6">
              <form
                onSubmit={profileForm.handleSubmit(handleProfileSubmit)}
                className="space-y-8"
              >
                {/* Basic Information Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6 border-l-2 border-blue-200 dark:border-blue-800">
                    <div>
                      <Label htmlFor="firstName" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <User className="h-3.5 w-3.5" />
                        First Name *
                      </Label>
                      {isEditing ? (
                        <Input
                          id="firstName"
                          {...profileForm.register("firstName")}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.firstName}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="lastName" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <User className="h-3.5 w-3.5" />
                        Last Name *
                      </Label>
                      {isEditing ? (
                        <Input
                          id="lastName"
                          {...profileForm.register("lastName")}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.lastName}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6 border-l-2 border-blue-200 dark:border-blue-800">
                    <div>
                      <Label htmlFor="email" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <Mail className="h-3.5 w-3.5" />
                        Email
                      </Label>
                      {isEditing ? (
                        <Input
                          id="email"
                          type="email"
                          {...profileForm.register("email")}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.email || "Not provided"}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="phone" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <Phone className="h-3.5 w-3.5" />
                        Phone Number
                      </Label>
                      {isEditing ? (
                        <Controller
                          control={profileForm.control}
                          name="phone"
                          render={({ field }) => {
                            const handlePhoneChange = (
                              event: ChangeEvent<HTMLInputElement>
                            ) => {
                              const formattedValue = formatPhoneInputValue(
                                event.target.value
                              );
                              field.onChange(formattedValue);
                            };
                            return (
                              <Input
                                id="phone"
                                {...field}
                                value={field.value || ""}
                                className="mt-1"
                                placeholder="(555) 123-4567"
                                onChange={handlePhoneChange}
                              />
                            );
                          }}
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.phone || "Not provided"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Personal Details Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Personal Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6 border-l-2 border-blue-200 dark:border-blue-800">
                    <div>
                      <Label htmlFor="dateOfBirth" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <Calendar className="h-3.5 w-3.5" />
                        Date of Birth
                      </Label>
                      {isEditing ? (
                        <Input
                          id="dateOfBirth"
                          type="date"
                          {...profileForm.register("dateOfBirth")}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.dateOfBirth
                            ? formatDateDisplay(employee.dateOfBirth)
                            : "Not provided"}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="ssn" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <Shield className="h-3.5 w-3.5" />
                        SSN
                      </Label>
                      {isEditing ? (
                        <SsnInput
                          id="ssn"
                          employeeId={employee.id}
                          className="mt-1"
                          value={profileForm.watch("ssn") || ""}
                          onChange={(v) => profileForm.setValue("ssn", v, { shouldDirty: true })}
                          onRevealed={setRevealedSsn}
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.ssn || "Not provided"}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="sex" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <User className="h-3.5 w-3.5" />
                        Sex
                      </Label>
                      {isEditing ? (
                        <Controller
                          control={profileForm.control}
                          name="sex"
                          render={({ field }) => (
                            <Select
                              value={field.value || ""}
                              onValueChange={field.onChange}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select sex" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="male">Male</SelectItem>
                                <SelectItem value="female">Female</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.sex || "Not provided"}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="maritalStatus" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <Heart className="h-3.5 w-3.5" />
                        Marital Status
                      </Label>
                      {isEditing ? (
                        <Controller
                          control={profileForm.control}
                          name="maritalStatus"
                          render={({ field }) => (
                            <Select
                              value={field.value || ""}
                              onValueChange={field.onChange}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select marital status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="single">Single</SelectItem>
                                <SelectItem value="married">Married</SelectItem>
                                <SelectItem value="divorced">Divorced</SelectItem>
                                <SelectItem value="widowed">Widowed</SelectItem>
                                <SelectItem value="separated">Separated</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.maritalStatus || "Not provided"}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="tobaccoUser" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <AlertCircle className="h-3.5 w-3.5" />
                        Tobacco User
                      </Label>
                      {isEditing ? (
                        <Controller
                          control={profileForm.control}
                          name="tobaccoUser"
                          render={({ field }) => (
                            <Select
                              value={field.value || ""}
                              onValueChange={field.onChange}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select option" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Yes">Yes</SelectItem>
                                <SelectItem value="No">No</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {(employee as any).tobacco_user || employee.tobaccoUser || "Not provided"}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="disabled" className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <Shield className="h-3.5 w-3.5" />
                        Disabled
                      </Label>
                      {isEditing ? (
                        <Controller
                          control={profileForm.control}
                          name="disabled"
                          render={({ field }) => (
                            <Select
                              value={field.value || ""}
                              onValueChange={field.onChange}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select option" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Yes">Yes</SelectItem>
                                <SelectItem value="No">No</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {(employee as any).disabled || "Not provided"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Address Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Address
                  </h3>
                  <div className="pl-6 border-l-2 border-blue-200 dark:border-blue-800">
                    {isEditing ? (
                      <div className="mt-2">
                        <AddressFields control={profileForm.control} />
                      </div>
                    ) : profileAddressDisplayParts.length > 0 ? (
                      <div className="mt-2 space-y-1">
                        {profileAddressDisplayParts.map((line) => (
                          <p key={line} className="text-base font-medium text-gray-900 dark:text-gray-100">
                            {line}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">Not provided</p>
                    )}
                  </div>
                </div>
              </form>
              </CardContent>
            </Card>

          {/* Emergency Contacts Section */}
          <Card className="border-l-4 border-l-orange-500 mt-6">
            <CardHeader className="bg-orange-50 dark:bg-orange-950/20 flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-orange-600" />
                Emergency Contacts ({emergencyContacts.length})
              </CardTitle>
              <Button 
                size="sm" 
                className="gap-2"
                onClick={() => {
                  setEditingEmergencyContact(null);
                  setShowAddEmergencyContact(true);
                }}
              >
                <Plus className="h-4 w-4" />
                Add Contact
              </Button>
            </CardHeader>
            <CardContent>
              {emergencyContacts.length > 0 ? (
                <div className="space-y-4">
                  {emergencyContacts.map((contact: any) => (
                    <div
                      key={contact.id}
                      className="border rounded-lg p-4 hover:bg-muted/50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <h4 className="font-medium">{contact.name}</h4>
                            {contact.relationship && (
                              <Badge variant="outline" className="capitalize">
                                {contact.relationship}
                              </Badge>
                            )}
                            {contact.contactType && (
                              <Badge 
                                variant={contact.contactType === "Primary" ? "default" : "secondary"}
                                className="capitalize"
                              >
                                {contact.contactType}
                              </Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Phone</p>
                              <p>{contact.phone || "Not provided"}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Email</p>
                              <p>{contact.email || "Not provided"}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Address</p>
                              <p>{contact.address || "Not provided"}</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditEmergencyContact(contact)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              handleDeleteEmergencyContact(contact.id)
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">
                    No Emergency Contacts
                  </h3>
                  <p className="text-sm">
                    Add emergency contact information for this employee.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Employment Tab */}
        <TabsContent value="employment">
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="bg-green-50 dark:bg-green-950/20 flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-green-600" />
                Employment Information
              </CardTitle>
              {!isEditing ? (
                <Button
                  size="sm"
                  className="gap-2"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit3 className="h-4 w-4" />
                  Edit Employment
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={() => setIsEditing(false)}
                    variant="outline"
                    size="sm"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => employmentForm.handleSubmit(handleEmploymentSubmit, handleEmploymentInvalid)()}
                  >
                    <Save className="h-4 w-4" />
                    Save
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-4">
              <form
                onSubmit={employmentForm.handleSubmit(handleEmploymentSubmit, handleEmploymentInvalid)}
                className="space-y-8"
              >

                {/* Job Information Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    Job Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6 border-l-2 border-blue-200 dark:border-blue-800">
                    <div>
                      <Label
                        htmlFor="position"
                        className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400"
                      >
                        <Briefcase className="h-3.5 w-3.5" />
                        Position *
                      </Label>
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <Select
                            value={employmentForm.watch("jobTitleId")?.toString() || ""}
                            onValueChange={(value) =>
                              employmentForm.setValue(
                                "jobTitleId",
                                value ? parseInt(value) : null,
                                { shouldDirty: true }
                              )
                            }
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue
                                placeholder={employee.position || "Select job title"}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {(orgJobTitles || []).map((jt: any) => (
                                <SelectItem key={jt.id} value={String(jt.id)}>
                                  {jt.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Dialog
                            open={isAddJobTitleOpen}
                            onOpenChange={setIsAddJobTitleOpen}
                          >
                            <DialogTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                disabled={!orgOwnerPayload}
                              >
                                Add
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Add Job Title</DialogTitle>
                              </DialogHeader>
                              <div className="flex gap-2 items-end">
                                <div className="flex-1">
                                  <Label>Name</Label>
                                  <Input
                                    value={newJobTitle}
                                    onChange={(e) => setNewJobTitle(e.target.value)}
                                  />
                                </div>
                                <Button
                                  type="button"
                                  disabled={!newJobTitle.trim() || addJobTitle.isPending}
                                  onClick={() =>
                                    newJobTitle.trim() &&
                                    addJobTitle.mutate(newJobTitle.trim())
                                  }
                                >
                                  Save
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.position || "Not provided"}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor="department"
                        className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400"
                      >
                        <Building2 className="h-3.5 w-3.5" />
                        Department
                      </Label>
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <Select
                            value={employmentForm.watch("departmentId")?.toString() || ""}
                            onValueChange={(value) =>
                              employmentForm.setValue(
                                "departmentId",
                                value ? parseInt(value) : null,
                                { shouldDirty: true }
                              )
                            }
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue
                                placeholder={employee.department || "Select department"}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {(orgDepartments || []).map((d: any) => (
                                <SelectItem key={d.id} value={String(d.id)}>
                                  {d.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Dialog
                            open={isAddDepartmentOpen}
                            onOpenChange={setIsAddDepartmentOpen}
                          >
                            <DialogTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                disabled={!orgOwnerPayload}
                              >
                                Add
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Add Department</DialogTitle>
                              </DialogHeader>
                              <div className="flex gap-2 items-end">
                                <div className="flex-1">
                                  <Label>Name</Label>
                                  <Input
                                    value={newDepartment}
                                    onChange={(e) => setNewDepartment(e.target.value)}
                                  />
                                </div>
                                <Button
                                  type="button"
                                  disabled={!newDepartment.trim() || addDepartment.isPending}
                                  onClick={() =>
                                    newDepartment.trim() &&
                                    addDepartment.mutate(newDepartment.trim())
                                  }
                                >
                                  Save
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.department || "Not provided"}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor="manager"
                        className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400"
                      >
                        <Users className="h-3.5 w-3.5" />
                        Manager
                      </Label>
                      {isEditing ? (
                        <Select
                          value={employmentForm.watch("managerEmployeeId")?.toString() || "__none__"}
                          onValueChange={(value) =>
                            employmentForm.setValue(
                              "managerEmployeeId",
                              value && value !== "__none__" ? parseInt(value) : null,
                              { shouldDirty: true }
                            )
                          }
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select manager" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">No manager</SelectItem>
                            {potentialManagers.map((mgr: Employee) => (
                              <SelectItem key={mgr.id} value={String(mgr.id)}>
                                {mgr.firstName} {mgr.lastName}
                                {mgr.position ? ` - ${mgr.position}` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {(() => {
                            const manager = potentialManagers.find(
                              (m: Employee) => m.id === (employee as any)?.managerEmployeeId
                            );
                            return manager
                              ? `${manager.firstName} ${manager.lastName}`
                              : "Not assigned";
                          })()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Work Details Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Work Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6 border-l-2 border-blue-200 dark:border-blue-800">
                    <div>
                      <Label
                        htmlFor="employmentType"
                        className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400"
                      >
                        <Clock className="h-3.5 w-3.5" />
                        Employment Type
                      </Label>
                      {isEditing ? (
                        <>
                          <Select
                            value={employmentForm.watch("employmentType") || ""}
                            onValueChange={(value) =>
                              employmentForm.setValue(
                                "employmentType",
                                normalizeEmploymentType(value) || value,
                                { shouldDirty: true }
                              )
                            }
                          >
                            <SelectTrigger
                              className={`mt-1 ${employmentForm.formState.errors.employmentType ? "border-destructive" : ""}`}
                            >
                              <SelectValue placeholder="Select employment type" />
                            </SelectTrigger>
                            <SelectContent>
                              {EMPLOYMENT_TYPE_OPTIONS_SHORT.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {employmentForm.formState.errors.employmentType && (
                            <p className="mt-1 text-sm text-destructive">
                              {(employmentForm.formState.errors.employmentType.message as string) || "Employment type is required"}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.employmentType || "Not provided"}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor="locationId"
                        className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400"
                      >
                        <MapPin className="h-3.5 w-3.5" />
                        Location
                      </Label>
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <Select
                            value={employmentForm.watch("locationId")?.toString() || ""}
                            onValueChange={(value) =>
                              employmentForm.setValue(
                                "locationId",
                                value ? parseInt(value) : null,
                                { shouldDirty: true }
                              )
                            }
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select location" />
                            </SelectTrigger>
                            <SelectContent>
                              {(orgLocations || []).map((l: any) => (
                                <SelectItem key={l.id} value={String(l.id)}>
                                  {formatOrgLocation(l)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Dialog
                            open={isAddLocationOpen}
                            onOpenChange={setIsAddLocationOpen}
                          >
                            <DialogTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                disabled={!orgOwnerPayload}
                              >
                                Add
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Add Location</DialogTitle>
                              </DialogHeader>
                              <div className="grid grid-cols-3 gap-2">
                                <div>
                                  <Label>Office</Label>
                                  <Input
                                    value={newLocOffice}
                                    onChange={(e) => setNewLocOffice(e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label>City</Label>
                                  <Input
                                    value={newLocCity}
                                    onChange={(e) => setNewLocCity(e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label>State</Label>
                                  <Input
                                    value={newLocState}
                                    onChange={(e) => setNewLocState(e.target.value)}
                                  />
                                </div>
                              </div>
                              <div className="mt-2">
                                <Button
                                  type="button"
                                  disabled={addLocation.isPending}
                                  onClick={() =>
                                    addLocation.mutate({
                                      officeName: newLocOffice || undefined,
                                      city: newLocCity || undefined,
                                      state: newLocState || undefined,
                                    })
                                  }
                                >
                                  Save
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {(orgLocations || []).find(
                            (l: any) => l.id === (employee as any)?.locationId
                          )
                            ? formatOrgLocation(
                                (orgLocations || []).find(
                                  (l: any) =>
                                    l.id === (employee as any)?.locationId
                                )
                              )
                            : "Not provided"}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor="employeeClassId"
                        className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400"
                      >
                        <Users className="h-3.5 w-3.5" />
                        Employee Class
                      </Label>
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <Select
                            value={
                              employmentForm.watch("employeeClassId")?.toString() ||
                              ""
                            }
                            onValueChange={(value) =>
                              employmentForm.setValue(
                                "employeeClassId",
                                value ? parseInt(value) : null,
                                { shouldDirty: true }
                              )
                            }
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select employee class" />
                            </SelectTrigger>
                            <SelectContent>
                              {(orgEmployeeClasses || []).map((c: any) => (
                                <SelectItem key={c.id} value={String(c.id)}>
                                  {c.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Dialog open={isAddClassOpen} onOpenChange={setIsAddClassOpen}>
                            <DialogTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                disabled={!orgOwnerPayload}
                              >
                                Add
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Add Employee Class</DialogTitle>
                              </DialogHeader>
                              <div className="flex gap-2 items-end">
                                <div className="flex-1">
                                  <Label>Name</Label>
                                  <Input
                                    value={newClass}
                                    onChange={(e) => setNewClass(e.target.value)}
                                  />
                                </div>
                                <Button
                                  type="button"
                                  disabled={!newClass.trim() || addEmployeeClass.isPending}
                                  onClick={() =>
                                    newClass.trim() &&
                                    addEmployeeClass.mutate(newClass.trim())
                                  }
                                >
                                  Save
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {(orgEmployeeClasses || []).find(
                            (c: any) => c.id === (employee as any)?.employeeClassId
                          )?.name || "Not provided"}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor="divisionId"
                        className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400"
                      >
                        <Building2 className="h-3.5 w-3.5" />
                        Division
                      </Label>
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <Select
                            value={employmentForm.watch("divisionId")?.toString() || ""}
                            onValueChange={(value) =>
                              employmentForm.setValue(
                                "divisionId",
                                value ? parseInt(value) : null,
                                { shouldDirty: true }
                              )
                            }
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select division" />
                            </SelectTrigger>
                            <SelectContent>
                              {(orgDivisions || []).map((d: any) => (
                                <SelectItem key={d.id} value={String(d.id)}>
                                  {d.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Dialog
                            open={isAddDivisionOpen}
                            onOpenChange={setIsAddDivisionOpen}
                          >
                            <DialogTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                disabled={!orgOwnerPayload}
                              >
                                Add
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Add Division</DialogTitle>
                              </DialogHeader>
                              <div className="flex gap-2 items-end">
                                <div className="flex-1">
                                  <Label>Name</Label>
                                  <Input
                                    value={newDivision}
                                    onChange={(e) => setNewDivision(e.target.value)}
                                  />
                                </div>
                                <Button
                                  type="button"
                                  disabled={!newDivision.trim() || addDivision.isPending}
                                  onClick={() =>
                                    newDivision.trim() &&
                                    addDivision.mutate(newDivision.trim())
                                  }
                                >
                                  Save
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {(orgDivisions || []).find(
                            (d: any) => d.id === (employee as any)?.divisionId
                          )?.name || "Not provided"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Schedule & Compensation Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Schedule & Compensation
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6 border-l-2 border-blue-200 dark:border-blue-800">
                    <div>
                      <Label
                        htmlFor="scheduledHours"
                        className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400"
                      >
                        <Clock className="h-3.5 w-3.5" />
                        Scheduled Hours / Week
                      </Label>
                      {isEditing ? (
                        <Input
                          id="scheduledHours"
                          {...employmentForm.register("scheduledHours")}
                          className="mt-1"
                          placeholder="e.g. 40"
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.scheduledHours || "Not provided"}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor="compensationType"
                        className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400"
                      >
                        <CreditCard className="h-3.5 w-3.5" />
                        Compensation Type
                      </Label>
                      {isEditing ? (
                        <Select
                          value={employmentForm.watch("compensationType") || ""}
                          onValueChange={(value) =>
                            employmentForm.setValue("compensationType", value, {
                              shouldDirty: true,
                            })
                          }
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select compensation type" />
                          </SelectTrigger>
                          <SelectContent>
                            {COMPENSATION_TYPE_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.compensationType || "Not provided"}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor="compensationAmount"
                        className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400"
                      >
                        <DollarSign className="h-3.5 w-3.5" />
                        Compensation Amount
                      </Label>
                      {isEditing ? (
                        <Input
                          id="compensationAmount"
                          {...employmentForm.register("compensationAmount")}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.compensationAmount || employee.salary
                            ? new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(
                                Number(employee.compensationAmount || employee.salary)
                              )
                            : "Not provided"}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor="payCycle"
                        className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400"
                      >
                        <Calendar className="h-3.5 w-3.5" />
                        Pay Cycle
                      </Label>
                      {isEditing ? (
                        <Select
                          value={employmentForm.watch("payCycle") || ""}
                          onValueChange={(value) =>
                            employmentForm.setValue("payCycle", value, {
                              shouldDirty: true,
                            })
                          }
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select pay cycle" />
                          </SelectTrigger>
                          <SelectContent>
                            {PAY_CYCLE_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.payCycle || "Not provided"}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor="payGroupId"
                        className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400"
                      >
                        <Calendar className="h-3.5 w-3.5" />
                        Pay Group
                      </Label>
                      {isEditing ? (
                        <Select
                          value={
                            employmentForm.watch("payGroupId") == null
                              ? "default"
                              : String(employmentForm.watch("payGroupId"))
                          }
                          onValueChange={(value) =>
                            employmentForm.setValue(
                              "payGroupId",
                              value === "default" ? null : Number(value),
                              { shouldDirty: true },
                            )
                          }
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select pay group" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">Client default</SelectItem>
                            {payGroups.map((group: any) => (
                              <SelectItem key={group.id} value={String(group.id)}>
                                {group.name}
                                {group.isDefault ? " (default)" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {payGroups.find((g: any) => g.id === (employee as any).payGroupId)?.name ||
                            "Client default"}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="pl-6">
                    {/* Employer edit button on this page is not permission-gated, so the card follows suit. */}
                    <CompensationHistoryCard employeeId={employee.id} canEdit={true} />
                  </div>
                </div>

                {/* Dates & Identification Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Dates & Identification
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-6 border-l-2 border-blue-200 dark:border-blue-800">
                    <div>
                      <Label
                        htmlFor="hireDate"
                        className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400"
                      >
                        <Calendar className="h-3.5 w-3.5" />
                        Hire Date
                      </Label>
                      {isEditing ? (
                        <Input
                          id="hireDate"
                          type="date"
                          {...employmentForm.register("hireDate")}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.hireDate
                            ? formatDateDisplay(employee.hireDate)
                            : "Not provided"}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor="employeeId"
                        className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400"
                      >
                        <IdCard className="h-3.5 w-3.5" />
                        Employee ID
                      </Label>
                      {isEditing ? (
                        <Input
                          id="employeeId"
                          {...employmentForm.register("employeeId")}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {employee.employeeId || "Not assigned"}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor="status"
                        className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400"
                      >
                        <Briefcase className="h-3.5 w-3.5" />
                        Employment Status
                      </Label>
                      {isEditing ? (
                        <Select
                          value={employmentForm.watch("status") || ""}
                          onValueChange={(value) =>
                            employmentForm.setValue("status", value, {
                              shouldDirty: true,
                            })
                          }
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            {EMPLOYEE_STATUS_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                          {formatStatus(employee.status) || "Not provided"}
                        </p>
                      )}
                    </div>

                    {/* Termination Details - only shown when employee is terminated */}
                    {employee.status === "terminated" && (
                      <>
                        <div>
                          <Label className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                            <Calendar className="h-3.5 w-3.5" />
                            Termination Date
                          </Label>
                          <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                            {employee.terminationDate 
                              ? formatDateDisplay(employee.terminationDate) 
                              : "Not provided"}
                          </p>
                        </div>

                        <div>
                          <Label className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                            <AlertCircle className="h-3.5 w-3.5" />
                            Termination Reason
                          </Label>
                          <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                            {employee.terminationReason 
                              ? employee.terminationReason.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())
                              : "Not provided"}
                          </p>
                        </div>

                        {employee.terminationNotes && (
                          <div className="col-span-2">
                            <Label className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                              <FileText className="h-3.5 w-3.5" />
                              Termination Notes
                            </Label>
                            <p className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                              {employee.terminationNotes}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit">Save Changes</Button>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Benefits Tab */}
        <TabsContent value="benefits">
          {/* Date of Birth Required Banner */}
          {employee && !employee.dateOfBirth && (
            <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20 mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                  <AlertTriangle className="w-5 h-5" />
                  Date of Birth Required
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-amber-700 dark:text-amber-300 mb-4">
                  This employee's date of birth is required to calculate benefits premiums and determine eligibility.
                  Please update their profile before configuring benefits.
                </p>
                <Button
                  variant="outline"
                  className="border-amber-600 text-amber-700 hover:bg-amber-100"
                  onClick={() => setActiveTab("profile")}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Update Profile
                </Button>
              </CardContent>
            </Card>
          )}

          {(() => {
            const totalTypes = offeredTypeIds.size;
            const configuredTypes = Math.max(0, totalTypes - typeSections.remainingCount);
            return (
              <div className="space-y-6">
                {/* Coverage banner — Configure Benefits CTA lives inside */}
                <CoverageHeaderBanner
                  totalTypes={totalTypes}
                  configuredTypes={configuredTypes}
                  segments={typeSections.segmentCounts}
                  totalMonthly={totals.emp || 0}
                  payCycle={employee?.payCycle}
                  unpricedPlans={unpricedPlans}
                  configureCta={{
                    remainingCount: typeSections.remainingCount,
                    onClick: () => openConfigureBenefits(null),
                  }}
                />

                {/* Upcoming Benefits — kept as a top-level section. */}
                {pendingEnrollments.length > 0 && (
                  <Card className="border-l-4 border-l-blue-500">
                    <CardHeader className="bg-blue-50 dark:bg-blue-950/20">
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-blue-600" />
                        Upcoming Benefits
                        <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-700">
                          {pendingEnrollments.length}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="space-y-3">
                        {(pendingEnrollments as any[]).map((benefit: any) => (
                          <ActiveBenefitRow
                            key={benefit.id}
                            benefit={benefit}
                            employee={employee}
                            dependents={dependents as any}
                            employeeBenefits={rawEnrollments as any}
                            onModify={() => openModifyForType(benefit?.plan?.type || "")}
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Type-sectioned elections */}
                <div className="space-y-5">
                  {typeSections.sections
                    .filter((s) => s.activeRows.length > 0 || s.waivedRows.length > 0 || s.emptyTypes.length > 0)
                    .map((section) => {
                      const hasAnyRow = section.activeRows.length > 0 || section.waivedRows.length > 0;
                      const defaultOpen = !section.def.collapsible || hasAnyRow;
                      return (
                        <BenefitSection
                          key={section.def.id}
                          def={section.def}
                          counts={{
                            enrolled: section.activeRows.length,
                            pending: section.pendingCount,
                            waived: section.waivedRows.length,
                            unconfigured: section.emptyTypes.length,
                          }}
                          defaultOpen={defaultOpen}
                          onConfigureSection={
                            section.emptyTypes.length > 0
                              ? () => openConfigureBenefits(section.def.types as unknown as string[])
                              : undefined
                          }
                        >
                          {section.activeRows.map((b: any) => (
                            <ActiveBenefitRow
                              key={b.id}
                              benefit={b}
                              employee={employee}
                              dependents={dependents as any}
                              employeeBenefits={rawEnrollments as any}
                              onModify={() => openModifyForType(b?.plan?.type || "")}
                            />
                          ))}
                          {section.waivedRows.map((b: any) => {
                            const pid = Number(b?.planId);
                            const needsConfig =
                              resolveNeedsConfigReason({
                                loaded: planContributionsFetched && Number.isFinite(pid),
                                isFsa: isFsaPlanType(String(b?.plan?.type || "")),
                                isLifeDisability: isLifeDisabilityType(String(b?.plan?.type || "")),
                                contributionCount: (planContributions as any[]).filter(
                                  (c: any) => c.planId === pid
                                ).length,
                                // no premium recompute on waived rows → coverage unknown
                                coverageAmountResolved: null,
                              }) !== null;
                            return (
                              <WaivedBenefitRow
                                key={b.id}
                                benefit={b}
                                needsConfig={needsConfig}
                                onEditWaiver={() => openModifyForType(b?.plan?.type || "")}
                                onEnrollInstead={() => openModifyForType(b?.plan?.type || "")}
                              />
                            );
                          })}
                          {section.emptyTypes.map((t) => {
                            const count = plansForType(t).length;
                            return (
                              <EmptyTypeRow
                                key={`empty-${section.def.id}-${t}`}
                                planType={t}
                                availablePlanCount={count}
                                onConfigure={() => openConfigureBenefits([t])}
                              />
                            );
                          })}
                        </BenefitSection>
                      );
                    })}
                </div>

                {/* Payroll Deduction Summary */}
                <Card className="border-l-4 border-l-green-500">
                  <CardHeader className="bg-green-50 dark:bg-green-950/20">
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-green-600" />
                      {hasNonMonthlyPayCycle(employee?.payCycle)
                        ? `${getPayCycleLabelCapitalized(employee?.payCycle)} Deduction Summary`
                        : "Monthly Cost Summary"}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">
                      Current {getPayCycleLabel(employee?.payCycle)} deductions from payroll for active benefit enrollments.
                    </p>
                  </CardHeader>
                  <CardContent className="p-6">
                    <DeductionSummaryContent
                      items={deductionItems}
                      totalMonthly={totals.emp || 0}
                      payCycle={employee?.payCycle}
                    />
                  </CardContent>
                </Card>

                {/* Configure-Benefits wizard dialog (scoped or global). */}
                <Dialog
                  open={showConfigureBenefits}
                  onOpenChange={(o) => {
                    setShowConfigureBenefits(o);
                    if (!o) setConfigureScope(null);
                  }}
                >
                  <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        Configure Benefits for {employee?.firstName} {employee?.lastName}
                      </DialogTitle>
                      <DialogDescription>
                        Select and configure benefit plans for this employee. Add plans not currently enrolled or waived.
                      </DialogDescription>
                    </DialogHeader>
                    <BenefitsConfigurationForm
                      employee={employee}
                      dependents={dependents as Dependent[]}
                      availablePlans={clientPlans as any[]}
                      existingEnrollments={baseEnrollments as any[]}
                      isLoading={isLoadingClientPlans}
                      restrictToTypes={configureScope ?? undefined}
                      onComplete={() => {
                        queryClient.invalidateQueries({
                          queryKey: ["/api/enrollments", "employee", employeeId],
                        });
                        setShowConfigureBenefits(false);
                        setConfigureScope(null);
                      }}
                    />
                  </DialogContent>
                </Dialog>

                {/* Unified per-type Modify modal */}
                {modifyContext && (
                  <ModifyTypeModal
                    open={!!modifyContext}
                    onClose={() => setModifyContext(null)}
                    employee={employee}
                    dependents={dependents as any}
                    employeeBenefits={rawEnrollments as any}
                    type={modifyContext.type}
                    currentEnrollment={modifyContext.current}
                    waivedEnrollment={modifyContext.waived}
                    availablePlans={plansForType(modifyContext.type)}
                    onSaved={() => {
                      queryClient.invalidateQueries({
                        queryKey: ["/api/enrollments", "employee", employeeId],
                      });
                    }}
                  />
                )}
              </div>
            );
          })()}
        </TabsContent>

        {/* Dependents Tab */}
        <TabsContent value="dependents">
          <Card className="border-l-4 border-l-yellow-500">
            <CardHeader className="bg-yellow-50 dark:bg-yellow-950/20">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-yellow-600" />
                  Dependents
                </CardTitle>
                <Button
                  onClick={() => setShowAddDependent(true)}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Dependent
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {dependents.length > 0 ? (
                <div className="space-y-4 mt-6">
                  {dependents.map((dependent: any) => (
                    <div key={dependent.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 flex-1">
                          <div>
                            <div className="text-sm font-medium">Name</div>
                            <div className="text-sm text-muted-foreground">
                              {dependent.firstName} {dependent.lastName}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm font-medium">
                              Relationship
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {dependent.relationship}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm font-medium">
                              Date of Birth
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {dependent.dateOfBirth
                                ? formatDateDisplay(dependent.dateOfBirth)
                                : "Not specified"}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm font-medium">Sex</div>
                            <div className="text-sm text-muted-foreground">
                              {dependent.sex || "Not specified"}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditDependent(dependent)}
                            className="gap-1"
                          >
                            <Edit3 className="h-3 w-3" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteDependent(dependent.id)}
                            className="gap-1 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-3 w-3" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No dependents on record</p>
                  <p className="text-sm mb-4">
                    Add dependents for this employee.
                  </p>
                  <Button
                    onClick={() => setShowAddDependent(true)}
                    className="gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add First Dependent
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ACA Tab */}
        <TabsContent value="aca">
          <EmployeeAcaSummary clientId={employee?.clientId} employeeId={employeeId} />
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card className="border-l-4 border-l-indigo-500">
            <CardHeader className="bg-indigo-50 dark:bg-indigo-950/20">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-indigo-600" />
                History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {Array.isArray(historyLogs) && historyLogs.length > 0 ? (
                <div className="space-y-4">
                  {redactSsnDeep([...historyLogs]).reverse().map((log: any) => (
                    <div key={log.id} className="border rounded-lg p-4">
                      {log.description && (
                        <div className="text-sm font-medium mb-2">
                          {log.description}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground mb-3">
                        {new Date(log.timestamp).toLocaleString()}
                        {log.changedBy && (
                          <span className="ml-2">
                            · Changed by: <span className="font-medium">{log.changedBy}</span>
                          </span>
                        )}
                      </div>
                      {log.changes && (
                        <div className="text-sm">
                          <div className="bg-gray-50 dark:bg-gray-800 rounded p-3 text-xs space-y-1">
                            {(() => {
                              try {
                                const changes =
                                  typeof log.changes === "string"
                                    ? JSON.parse(log.changes)
                                    : log.changes;
                                return Object.entries(changes).map(
                                  ([field, change]: [string, any]) => (
                                    <div key={field} className="flex items-center gap-2">
                                      <span className="font-medium capitalize">
                                        {field}:
                                      </span>
                                      {change.from && (
                                        <span className="text-red-600 dark:text-red-400">
                                          {change.from}
                                        </span>
                                      )}
                                      {change.from && change.to && (
                                        <span className="text-gray-400">→</span>
                                      )}
                                      <span className="text-green-600 dark:text-green-400">
                                        {change.to}
                                      </span>
                                    </div>
                                  )
                                );
                              } catch {
                                return <div>{log.changes}</div>;
                              }
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No history available</p>
                  <p className="text-sm">
                    Employee changes and activities will appear here.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <DocumentsTabContent employeeId={employeeId || "0"} clientId={employee?.clientId === null ? undefined : employee?.clientId} employeeName={employee?.firstName && employee?.lastName ? `${employee.firstName} ${employee.lastName}` : "Employee"} />
      </Tabs>


      {/* Add Dependent to Existing Coverage Modal */}
      <Dialog
        open={showAddDependentToCoverage}
        onOpenChange={setShowAddDependentToCoverage}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Dependent to Existing Coverage</DialogTitle>
            <DialogDescription>
              Add dependents to {selectedCoverageForDependent?.type} -{" "}
              {selectedCoverageForDependent?.planName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Current Coverage Info */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <h4 className="font-medium mb-2">Current Coverage Details</h4>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span>Plan:</span>
                  <span>{selectedCoverageForDependent?.planName}</span>
                </div>
                <div className="flex justify-between">
                  <span>Current Level:</span>
                  <span>{selectedCoverageForDependent?.coverageLevel}</span>
                </div>
                <div className="flex justify-between">
                  <span>Current Premium:</span>
                  <span>
                    {selectedCoverageForDependent?.monthlyPremium}/month
                  </span>
                </div>
              </div>
            </div>

            {/* Available Dependents */}
            {dependents.length > 0 ? (
              <div className="space-y-3">
                <Label>Select Dependents to Add to Coverage</Label>
                <div className="space-y-3">
                  {dependents.map((dependent: any) => (
                    <div
                      key={dependent.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          id={"add-dependent-" + dependent.id}
                          checked={
                            selectedDependentIds.includes(dependent.id) ||
                            dependent.isEnrolled
                          }
                          onCheckedChange={(checked) =>
                            handleDependentSelection(
                              dependent.id,
                              checked as boolean
                            )
                          }
                          disabled={dependent.isEnrolled}
                        />
                        <div>
                          <Label
                            htmlFor={"add-dependent-" + dependent.id}
                            className="font-medium"
                          >
                            {dependent.firstName} {dependent.lastName}
                          </Label>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {dependent.relationship} • Born:{" "}
                            {formatDateDisplay(dependent.dateOfBirth)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        {dependent.isEnrolled ? (
                          <Badge variant="secondary">Already Enrolled</Badge>
                        ) : (
                          <Badge variant="outline">Available</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No dependents available</p>
                <p className="text-sm">
                  Add dependents in the Family section first.
                </p>
              </div>
            )}

            {/* Effective Date */}
            <div className="space-y-2">
              <Label>Effective Date for Coverage Change</Label>
              <Input
                type="date"
                defaultValue={new Date().toISOString().split("T")[0]}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAddDependentToCoverage(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={
                addDependentToCoverageMutation.isPending ||
                selectedDependentIds.length === 0
              }
              onClick={() => {
                // Automatically determine coverage tier based on dependents
                const hasSpouse = dependents.some(
                  (d: any) =>
                    d.relationship === "Spouse" &&
                    (d.isEnrolled || selectedDependentIds.includes(d.id))
                );
                const hasChildren = dependents.some(
                  (d: any) =>
                    d.relationship === "Child" &&
                    (d.isEnrolled || selectedDependentIds.includes(d.id))
                );

                const autoCoverageTier = hasSpouse && hasChildren
                  ? "family"
                  : hasSpouse
                  ? "employee_spouse"
                  : hasChildren
                  ? "employee_children"
                  : "employee_only";

                addDependentToCoverageMutation.mutate({
                  coverageId: selectedCoverageForDependent?.id,
                  planId: selectedCoverageForDependent?.planId,
                  dependentIds: selectedDependentIds,
                  coverageTier: autoCoverageTier,
                  effectiveDate: new Date().toISOString().split("T")[0],
                });
              }}
            >
              {addDependentToCoverageMutation.isPending
                ? "Adding..."
                : `Add ${selectedDependentIds.length} Dependent${
                    selectedDependentIds.length !== 1 ? "s" : ""
                  } to Coverage`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EmergencyContactModal
        open={showAddEmergencyContact}
        onOpenChange={(open) => {
          setShowAddEmergencyContact(open);
          if (!open) {
            setEditingEmergencyContact(null);
          }
        }}
        onSubmit={handleEmergencyContactSubmit}
        editingContact={editingEmergencyContact}
        employeeName={
          employee ? `${employee.firstName} ${employee.lastName}` : undefined
        }
        employeeAddress={
          employee
            ? {
                address1: employee.address1 || "",
                address2: employee.address2 || "",
                city: employee.city || "",
                state: employee.state || "",
                zipCode: employee.zipCode || "",
                county: employee.county || "",
                country: employee.country || "United States",
              }
            : undefined
        }
        isPending={
          createEmergencyContactMutation.isPending ||
          updateEmergencyContactMutation.isPending
        }
      />

      {/* Add Dependent Dialog */}
      <Dialog open={showAddDependent} onOpenChange={setShowAddDependent}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-yellow-600" />
              Add Dependent
            </DialogTitle>
            <DialogDescription>
              Add a dependent for {employee?.firstName} {employee?.lastName}
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={dependentForm.handleSubmit(handleAddDependent)}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dependentFirstName">First Name</Label>
                <Input
                  id="dependentFirstName"
                  {...dependentForm.register("firstName")}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="dependentLastName">Last Name</Label>
                <Input
                  id="dependentLastName"
                  {...dependentForm.register("lastName")}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="dependentRelationship">Relationship</Label>
                <Select
                  value={dependentForm.watch("relationship")}
                  onValueChange={(value) =>
                    dependentForm.setValue("relationship", value)
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Spouse">Spouse</SelectItem>
                    <SelectItem value="Child">Child</SelectItem>
                    <SelectItem value="Partner">Partner</SelectItem>
                    <SelectItem value="Stepchild">Stepchild</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="dependentDateOfBirth">Date of Birth</Label>
                <Input
                  id="dependentDateOfBirth"
                  type="date"
                  {...dependentForm.register("dateOfBirth")}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="dependentSex">Sex</Label>
                <Select
                  value={dependentForm.watch("sex")}
                  onValueChange={(value) =>
                    dependentForm.setValue("sex", value)
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select sex" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="dependentSSN">SSN (Optional)</Label>
                <Input
                  id="dependentSSN"
                  {...dependentForm.register("ssn")}
                  placeholder="XXX-XX-XXXX"
                  className="mt-1"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddDependent(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createDependentMutation.isPending}
              >
                {createDependentMutation.isPending
                  ? "Adding..."
                  : "Add Dependent"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Prompt: Add new dependent to benefits */}
      <Dialog
        open={showAddDependentBenefitsPrompt}
        onOpenChange={(open) => {
          if (!open) closeAddDependentBenefitsPrompt();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add dependent to benefits?</DialogTitle>
            <DialogDescription>
              Should
              {" "}
              {`${dependentPendingBenefits?.firstName || ""} ${
                dependentPendingBenefits?.lastName || ""
              }`.trim() || "this dependent"}
              {" "}
              be added to benefit coverage now?
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeAddDependentBenefitsPrompt}>
              Not now
            </Button>
            <Button type="button" onClick={confirmAddDependentToBenefits}>
              Add to benefits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dependent Dialog */}
      <Dialog
        open={!!editingDependent}
        onOpenChange={(open) => !open && setEditingDependent(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5 text-yellow-600" />
              Edit Dependent
            </DialogTitle>
            <DialogDescription>
              Update dependent information for {employee?.firstName}{" "}
              {employee?.lastName}
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={dependentForm.handleSubmit(handleUpdateDependent)}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="editDependentFirstName">First Name</Label>
                <Input
                  id="editDependentFirstName"
                  {...dependentForm.register("firstName")}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="editDependentLastName">Last Name</Label>
                <Input
                  id="editDependentLastName"
                  {...dependentForm.register("lastName")}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="editDependentRelationship">Relationship</Label>
                <Select
                  value={dependentForm.watch("relationship")}
                  onValueChange={(value) =>
                    dependentForm.setValue("relationship", value)
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Spouse">Spouse</SelectItem>
                    <SelectItem value="Child">Child</SelectItem>
                    <SelectItem value="Partner">Partner</SelectItem>
                    <SelectItem value="Stepchild">Stepchild</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="editDependentDateOfBirth">Date of Birth</Label>
                <Input
                  id="editDependentDateOfBirth"
                  type="date"
                  {...dependentForm.register("dateOfBirth")}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="editDependentSex">Sex</Label>
                <Select
                  value={dependentForm.watch("sex")}
                  onValueChange={(value) =>
                    dependentForm.setValue("sex", value)
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select sex" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="editDependentSSN">SSN (Optional)</Label>
                <SsnInput
                  id="editDependentSSN"
                  employeeId={employee.id}
                  dependentId={editingDependent?.id}
                  value={dependentForm.watch("ssn") || ""}
                  onChange={(v) => dependentForm.setValue("ssn", v, { shouldDirty: true })}
                  onRevealed={setRevealedDependentSsn}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Coverage management for this dependent */}
            <div className="space-y-2">
              <Label>Benefit Coverage</Label>
              {enrolledPlansForEditingDependent.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  This dependent is not currently enrolled in any coverages.
                </p>
              ) : (
                <div className="space-y-2">
                  {enrolledPlansForEditingDependent.map((p) => (
                    <div
                      key={p.planId}
                      className="flex items-center justify-between rounded-lg border p-3"
                    >
                      <div>
                        <div className="font-medium">{p.planName}</div>
                        <div className="text-xs text-muted-foreground">
                          Enrolled (rows: {p.rows.length})
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="destructive"
                        disabled={removeDependentFromCoverageMutation.isPending}
                        onClick={() => {
                          const dependentId = Number((editingDependent as any)?.id);
                          if (!Number.isFinite(dependentId)) return;
                          removeDependentFromCoverageMutation.mutate({
                            dependentId,
                            planId: p.planId,
                          });
                        }}
                      >
                        Remove from coverage
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditingDependent(null)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateDependentMutation.isPending}
              >
                {updateDependentMutation.isPending
                  ? "Updating..."
                  : "Update Dependent"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Terminate Employee Modal */}
      {employee && (
        <TerminateEmployeeModal
          open={showTerminateModal}
          onOpenChange={setShowTerminateModal}
          employee={{
            id: employee.id,
            firstName: employee.firstName || "",
            lastName: employee.lastName || "",
            hireDate: employee.hireDate || null,
            email: employee.email || undefined,
          }}
          activeEnrollments={activeEnrollmentsForTermination}
          onConfirm={handleTerminationConfirm}
          isProcessing={terminateMutation.isPending}
        />
      )}

      {/* Delete Employee Confirmation Dialog */}
      <Dialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Employee?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {employee?.firstName} {employee?.lastName}?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
              <p className="text-sm text-amber-900">
                <strong>Important:</strong> This employee can be deleted because they:
              </p>
              <ul className="list-disc list-inside text-sm text-amber-900 mt-2 space-y-1">
                <li>Have not been officially hired (no hire date)</li>
                <li>Have no benefit enrollments</li>
              </ul>
              <p className="text-sm text-amber-900 mt-2">
                This likely means they were added to the system but declined the job offer or never started employment.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                deleteEmployeeMutation.mutate();
                setShowDeleteConfirmation(false);
              }}
              disabled={deleteEmployeeMutation.isPending}
            >
              {deleteEmployeeMutation.isPending ? "Deleting..." : "Delete Employee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employment Change Verification Modal */}
      <EmploymentVerificationModal />
    </div>
  );
}
